import { useBackgroundCanvasWork } from "./background-canvas-status.js";
import { useCallback, useEffect, useRef, useState } from "react";

import {
  PlayerAIProblem,
  isPlayerAIRequestTimeout,
  type PlayerAIEventConnection,
} from "./client.js";
import { usePlayerAIClient } from "./provider.js";
import type { PlatformAction } from "@playbookai/types";
import { hasSingletonPrivateConversation } from "./types.js";
import {
  RUN_DEFERRED_CODE,
  describePlayerAIFailure,
  playerAIFailureCode,
  type PlayerAIFailure,
  type PlayerAIFailureSource,
  type PlayerAIFailureSurface,
} from "./failures.js";
import type {
  BackgroundReviewFailure,
  ContextCompactionNotice,
  Capabilities,
  CollaborationMode,
  ConnectionState,
  Conversation,
  ConversationHydration,
  ConversationProfileConfiguration,
  ConversationSpecialistSettings,
  ConversationSurface,
  GroundingSourceRef,
  GroupSchedule,
  GroupSettings,
  InteractionContext,
  MentionIndex,
  Message,
  MessageFeedback,
  MessageMention,
  ModelPolicyId,
  ModelPolicySummary,
  PlayerAIEvent,
  PlatformActionBatch,
  PlayerAIScope,
  Proposal,
  ProposalBatchDecisionItem,
  ProposalDecisionSelection,
  ProposalRevisionInput,
  Run,
  RunActivity,
  SolveResponseStyle,
  RunActivityKind,
  RunActivityServiceKind,
  RunActivityStatus,
  RunFailure,
  PlayerAIAuthorityStatus,
  RunProgress,
  TypingParticipant,
  UserSettings,
  UpsertMessageFeedbackInput,
  UsePlayerAIResult,
} from "./types.js";
import {
  MAX_MESSAGE_ATTACHMENTS,
  MAX_RUN_ACTIVITY_KIND_LENGTH,
  MAX_RUN_ACTIVITY_SAFE_COUNT,
  MAX_RUN_ACTIVITY_SAFE_LABEL_LENGTH,
} from "./types.js";

const ACTIVE_RUN_STATUSES = new Set([
  "queued",
  "running",
  "waiting_documents",
  "waiting_approval",
]);
const STREAMING_RUN_STATUSES = new Set(["queued", "running"]);
/**
 * d26: how long an active Run may go with NO transport activity — no byte on
 * the live line, no poll reply — before the hook re-reads it. Longer than the
 * client's 30 s quiet check, so a reconnect's hello lands first.
 */
const SILENCE_REREAD_MS = 40_000;
/**
 * The kind is validated for SHAPE, never for membership.
 *
 * An allowlist here is the rolling-deployment bug: the service starts emitting
 * an additive kind, an already-loaded tab does not know it, and every frame for
 * that activity is dropped — including its `completed`, so the panel keeps a row
 * spinning that has in fact finished. Bounding the string instead means an
 * unknown kind is transported intact and rendered generically, while a payload
 * still cannot smuggle a filename or a sentence through this field.
 */
const RUN_ACTIVITY_KIND_PATTERN = /^[a-z][a-z0-9_]*$/;

function isTransportableActivityKind(value: unknown): value is RunActivityKind {
  return (
    typeof value === "string" &&
    value.length > 0 &&
    value.length <= MAX_RUN_ACTIVITY_KIND_LENGTH &&
    RUN_ACTIVITY_KIND_PATTERN.test(value)
  );
}
const RUN_ACTIVITY_STATUS_VALUES = {
  started: true,
  completed: true,
  failed: true,
} satisfies Record<RunActivityStatus, true>;
const RUN_ACTIVITY_STATUSES = new Set<RunActivityStatus>(
  Object.keys(RUN_ACTIVITY_STATUS_VALUES) as RunActivityStatus[],
);
const GROUNDING_SOURCE_TYPES = new Set([
  "conversation_attachment",
  "vsm_preparation_source",
]);
/** Mirrors MAX_ACTIVE_ACTIVITIES in the Player AI hydration projection. */
const MAX_ACTIVE_ACTIVITIES = 12;
const DEFAULT_USER_SETTINGS: UserSettings = {
  auto_approve_internal_tools: true,
  model_policy_id: "reasoning-medium",
};
const SOLVE_CANVAS_SPECIALIST_ID = "solve-canvas-agent";

/**
 * Which product surface a scope belongs to, for the copied diagnostics.
 *
 * Derived from the scope the host already passed, never from a route or a
 * label: the value ends up in a block a user pastes into a ticket, and it is
 * bounded by `PlayerAIFailureSurface` so nothing else can reach it.
 */
function scopeSurface(
  scope: PlayerAIScope | null | undefined,
): PlayerAIFailureSurface | undefined {
  if (!scope) return undefined;
  if (!("workspaceType" in scope)) return "home";
  return scope.workspaceType;
}

/**
 * MODULE SCOPE on purpose.
 *
 * These are the replacements for `setError(errorMessage(…))`, and they are
 * plain functions rather than hook values so that swapping them in changes no
 * `useCallback` dependency array — a `useState` setter is stable and known to
 * be stable by the lint rule, and a hook-created callback is neither.
 */
function requestFailure(
  error: unknown,
  scope: PlayerAIScope | null | undefined,
  source: PlayerAIFailureSource = "read",
): PlayerAIFailure {
  // An ALREADY-CLASSIFIED failure keeps its code and correlation ids and is
  // only re-staged. Initialization re-raises the startup read's failure so the
  // one `catch` can do the common cleanup; re-running the raw classifier on
  // that wrapper would answer `unknown` and lose the server's own answer — the
  // exact reason a rethrow has to carry more than a sentence.
  if (error instanceof CarriedPlayerAIFailure) {
    const { errorCode, occurredAt, requestId, httpStatus } =
      error.failure.diagnostics;
    return describePlayerAIFailure({
      source,
      code: errorCode,
      occurredAt,
      requestId,
      httpStatus,
      surface: scopeSurface(scope),
    });
  }
  const classified = playerAIFailureCode(error);
  return describePlayerAIFailure({
    source,
    ...classified,
    surface: scopeSurface(scope),
  });
}

/** Re-raises an already-classified failure without flattening it to a string. */
class CarriedPlayerAIFailure extends Error {
  readonly failure: PlayerAIFailure;

  constructor(failure: PlayerAIFailure) {
    super(failure.title);
    this.name = "CarriedPlayerAIFailure";
    this.failure = failure;
  }
}

/** The durable failure of a Run, from either the live frame or a hydration. */
function terminalRunFailure(
  failure: RunFailure,
  scope: PlayerAIScope | null | undefined,
): PlayerAIFailure {
  return describePlayerAIFailure({
    source: "run",
    code: failure.error_code,
    occurredAt: failure.occurred_at,
    runId: failure.run_id,
    attemptCount: failure.attempt_epoch,
    runOutcome: failure.outcome,
    runtimeRelease: failure.runtime_release,
    surface: scopeSurface(scope),
    // Fail-closed: only the server's explicit `true` earns a retry. The
    // `requested_by_user_id` on this projection is deliberately NOT passed —
    // it gates the UI, and a user id must never reach a copied block.
    runRetrySafe: failure.retry_safe === true,
  });
}

/** One sentence, for the string-only `error` and `initializationError`. */
function failureSentence(failure: PlayerAIFailure): string {
  return `${failure.title} ${failure.message}`;
}

export function usePlayerAI(
  scope: PlayerAIScope | null | undefined,
): UsePlayerAIResult {
  const client = usePlayerAIClient();
  const [connectionState, setConnectionState] =
    useState<ConnectionState>("idle");
  const [streamReconnectAttempt, setStreamReconnectAttempt] = useState(0);
  /**
   * The current failure, whatever path reported it (PB-5276).
   *
   * One state rather than two: `error` used to be an independently-written
   * string, which is how a raw code, a problem `detail` and a serialized error
   * object all reached the panel. It is now DERIVED, so there is no way to
   * publish a sentence that did not come from the catalogue.
   */
  const [failure, setFailure] = useState<PlayerAIFailure | null>(null);
  const error = failure === null ? null : failureSentence(failure);
  /**
   * The failure that stopped this scope from ever OPENING a conversation
   * (PB-1586), as distinct from one that failed a Run or a send.
   *
   * They looked identical to the host — both arrived as `error` — so a
   * dependency outage during session setup degraded the panel to a read-only
   * composer whose placeholder said "This conversation is read-only.", which
   * is a statement about AUTHORIZATION. The user's only recourse was a full
   * page reload. A generation failure already offers its own retry; this is
   * the missing one, and it has to be separable because the two are recovered
   * differently: a Run is retried, initialization is re-run.
   */
  const [initializationError, setInitializationError] = useState<string | null>(
    null,
  );
  /**
   * Bumped by {@link retryInitialize}. It is a dependency of the initialization
   * effect, so incrementing it re-runs the whole setup — reset included —
   * without a page reload. A counter rather than a boolean: two retries in a
   * row must both fire, and a boolean toggled back to the same value would not
   * re-trigger the effect.
   */
  const [initializeAttempt, setInitializeAttempt] = useState(0);
  /**
   * Why the last {@link selectConversation} returned `false`, or `null` when it
   * returned `false` merely because a newer load superseded it (PB-1586).
   *
   * `selectConversation` catches its own hydration / message / proposal
   * failures and answers `false` — a documented contract hosts rely on — so a
   * resolution that SUCCEEDED followed by a startup read that failed never
   * reached initialization's `catch`, `initializationError` stayed `null`, and
   * the rail offered no "Retry connection" for an outage it could have
   * recovered from in place. The boolean alone cannot tell the two apart: a
   * superseded load is not a failure and must not arm the retry.
   */
  const selectFailureRef = useRef<PlayerAIFailure | null>(null);
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [activeConversationId, setActiveConversationId] = useState<
    string | null
  >(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [proposals, setProposals] = useState<Proposal[]>([]);
  // Fence list snapshots against live frames and decisions received during a read.
  const proposalRevisionsRef = useRef(new Map<string, number>());
  const mergeProposalSnapshot = useCallback(
    (page: Proposal[], observedRevisions: ReadonlyMap<string, number>) => {
      const accepted = page.filter(
        (proposal) =>
          (proposalRevisionsRef.current.get(proposal.id) ?? 0) ===
          (observedRevisions.get(proposal.id) ?? 0),
      );
      for (const proposal of accepted)
        proposalRevisionsRef.current.set(
          proposal.id,
          (proposalRevisionsRef.current.get(proposal.id) ?? 0) + 1,
        );
      setProposals((current) => accepted.reduce(upsertProposal, [...current]));
    },
    [],
  );
  const [platformActionBatch, setPlatformActionBatch] =
    useState<PlatformActionBatch | null>(null);
  const [run, setRun] = useState<Run | null>(null);
  const [capabilities, setCapabilities] = useState<Capabilities | null>(null);
  /**
   * The surface the SERVER routed this scope to, when it answered one.
   *
   * `null` for every scope that does not resolve its own surface, so a host
   * cannot mistake "not reported" for "private" — a wrong privacy label is
   * worse than no label.
   */
  const [conversationSurface, setConversationSurface] =
    useState<ConversationSurface | null>(null);
  /**
   * Whether the SERVER considers this actor the workspace's effective driver.
   *
   * Reported, never derived from a role: the drive moves with a facilitator
   * grant, and the owner is only the fallback. `false` until the server says
   * otherwise, so a host that reads it before resolution offers nothing rather
   * than offering something the next call would refuse.
   */
  const [isEffectiveDriver, setIsEffectiveDriver] = useState(false);
  // The SOLVE's shared tone already rides on `capabilities` from the authorize
  // call, so reading it costs no request. This override holds only the value a
  // local write just confirmed, until the next hydration refreshes capabilities.
  //
  // It is KEYED BY CONVERSATION. The hook outlives the conversation it is
  // showing — `selectConversation` swaps the active id and clears capabilities
  // — so an unkeyed value would still be returned after the switch and one
  // SOLVE's confirmed Direct would surface on the next SOLVE whose stored tone
  // is Empathetic. Reading it only for the matching id makes that structurally
  // impossible instead of relying on a clear-on-switch that is easy to forget.
  const [solveResponseStyleOverride, setSolveResponseStyleOverride] = useState<{
    readonly conversationId: string;
    readonly style: SolveResponseStyle;
  } | null>(null);
  // Counts style writes this hook has DURABLY CONFIRMED. A hydration samples it
  // before issuing its read; if the count moved by the time that read lands,
  // the snapshot predates a confirmed write and must not retire its override.
  const solveResponseStyleWriteGenerationRef = useRef(0);

  // Fresh capabilities ARE the authority, so a hydration or refresh for a
  // conversation retires the override it was bridging — but only when the
  // snapshot is not already stale. A refresh that started before a confirmed
  // write returns the pre-write tone, and clearing on it would make the server's
  // own accepted change disappear from the rail.
  const clearSolveResponseStyleOverride = useCallback(
    (conversationId: string, observedWriteGeneration: number): void => {
      if (
        observedWriteGeneration !== solveResponseStyleWriteGenerationRef.current
      ) {
        return;
      }
      setSolveResponseStyleOverride((current) =>
        current?.conversationId === conversationId ? null : current,
      );
    },
    [],
  );
  const [solveWebSearchOverride, setSolveWebSearchOverride] = useState<{
    readonly conversationId: string;
    readonly enabled: boolean;
  } | null>(null);
  const solveWebSearchWriteGenerationRef = useRef(0);
  const clearSolveWebSearchOverride = useCallback(
    (conversationId: string, observedWriteGeneration: number): void => {
      if (
        observedWriteGeneration !== solveWebSearchWriteGenerationRef.current
      ) {
        return;
      }
      setSolveWebSearchOverride((current) =>
        current?.conversationId === conversationId ? null : current,
      );
    },
    [],
  );
  const [eventCursor, setEventCursor] = useState(0);
  const [assistantDraft, setAssistantDraft] = useState("");
  const [runProgress, setRunProgress] = useState<RunProgress | null>(null);
  const [runActivities, setRunActivities] = useState<RunActivity[]>([]);
  const [runFailure, setRunFailure] = useState<RunFailure | null>(null);
  const [hasMoreMessages, setHasMoreMessages] = useState(false);
  const [isLoadingMessages, setIsLoadingMessages] = useState(false);
  const [isConversationLoading, setIsConversationLoading] = useState(false);
  const [isSubmittingMessage, setIsSubmittingMessage] = useState(false);
  const [settings, setSettings] = useState<UserSettings>({
    ...DEFAULT_USER_SETTINGS,
  });
  const [modelPolicies, setModelPolicies] = useState<ModelPolicySummary[]>([]);
  const [groupSettings, setGroupSettings] = useState<GroupSettings | null>(
    null,
  );
  const [backgroundCanvasSettings, setBackgroundCanvasSettings] =
    useState<ConversationSpecialistSettings | null>(null);
  const [conversationProfile, setConversationProfileState] =
    useState<ConversationProfileConfiguration | null>(null);
  const conversationProfileRef =
    useRef<ConversationProfileConfiguration | null>(null);
  const backgroundCanvasSettingsRef =
    useRef<ConversationSpecialistSettings | null>(null);
  const [backgroundReviewFailure, setBackgroundReviewFailure] =
    useState<BackgroundReviewFailure | null>(null);
  /* The thread's current context-compaction notice, or null. Last-one-wins:
     it describes the CURRENT state of this thread's context, not a history. */
  const [contextNotice, setContextNotice] =
    useState<ContextCompactionNotice | null>(null);
  /* Task 15: who is typing in a group conversation, as the server last said.
     Ephemeral — replaced wholesale by each frame, never persisted. */
  const [typingParticipants, setTypingParticipants] = useState<
    TypingParticipant[]
  >([]);
  /* One id per mounted hook — per tab — so this tab going quiet never clears
     another tab of the same person that is still typing. */
  const [typingSessionId] = useState(randomUuid);
  /* Counts typing frames, so a (re)connect read that a newer frame overtook
     cannot put the older state back. */
  const typingFrameRef = useRef(0);

  const canvasScope =
    scope && "workspaceType" in scope ? scope.workspaceType : null;
  const canvasWork = useBackgroundCanvasWork(
    client,
    canvasScope === "solve" ? activeConversationId : null,
  );

  const scopeRef = useRef(scope);
  const activeConversationRef = useRef<string | null>(null);
  const conversationSurfaceRef = useRef<ConversationSurface | null>(null);
  const beforeSequenceRef = useRef<number | null>(null);
  const loadGenerationRef = useRef(0);
  // One history request owns the paging cursor in each conversation load.
  const pagingGenerationRef = useRef<number | null>(null);
  const refreshGenerationRef = useRef(0);
  /**
   * PB-5296 D7, as revised by Task 7. CONFIRMED capabilities and REFRESH STATUS
   * are separate facts, and this is the status — set explicitly at each
   * transition, never derived from a boolean.
   *
   * The original decision masked every capability for the whole of a
   * revalidation, which is what disabled the composer, dropped keyboard focus
   * and silently discarded a file chosen while the picker was open — during a
   * refresh that turned out to CONFIRM the access it had just withdrawn. A
   * healthy check now keeps the last confirmed answer in force; the server
   * still gates every action, and a refusal is handled where it happens.
   *
   * Starts `checking`, not `valid`: an initial load has confirmed nothing, and
   * `capabilities` is `null` until the first hydrate, so nothing is granted
   * either way.
   */
  const [authorityStatus, setAuthorityStatus] =
    useState<PlayerAIAuthorityStatus>("checking");
  /** Masking applies to a FAILED or DENIED check — never to one in flight. */
  const authorityBlocked =
    authorityStatus === "unavailable" || authorityStatus === "denied";
  const autoApproveWriteGenerationRef = useRef(0);
  const modelPolicyWriteGenerationRef = useRef(0);
  const disconnectRef = useRef<PlayerAIEventConnection | null>(null);
  const runRef = useRef<Run | null>(null);
  /**
   * d30: counts every Run update that is NOT a hydration read — a live frame,
   * a Send, Stop or decision reply. A read samples it BEFORE it is issued; if
   * it moved by the time the read lands, the read is older than something
   * this client already saw.
   */
  const runObservationRef = useRef(0);
  /**
   * Reads are ordered among themselves too: each takes a sequence when it is
   * ISSUED, and once one has been applied, an earlier-issued read that lands
   * later is stale. Without it a slow safety re-read could retire a newer Run
   * a later Refresh already found.
   */
  const runReadSequenceRef = useRef(0);
  const appliedRunReadRef = useRef(0);
  /**
   * The Run a hydration read retired by answering "no active Run". Kept so a
   * late frame or reply for it cannot re-lock the composer after the visible
   * Run is gone.
   */
  const retiredRunRef = useRef<Pick<Run, "id" | "created_at"> | null>(null);
  /** d25: a 403/404 ended the transport; restart it once access is confirmed. */
  const transportRestartPendingRef = useRef(false);
  /** d26: when the transport last showed any sign of life. */
  const transportActivityAtRef = useRef(0);
  /** Lets the transport's handlers reach the refresh defined after them. */
  const refreshConversationRef = useRef<
    (permissionsOnly?: boolean) => Promise<void>
  >(() => Promise.resolve());
  const activityAttemptEpochRef = useRef(0);
  const assistantAttemptEpochRef = useRef(0);
  const assistantDeltaSequenceRef = useRef(0);
  /**
   * Whether an initialization for the current scope is still in flight.
   *
   * A REF, and set synchronously, because it gates {@link retryInitialize}
   * against ITSELF: a render value cannot, since two calls in the same tick —
   * or any call before the effect re-runs — would both read the pre-retry
   * state. The failure it closes is a duplicate resource, not a duplicate
   * request: a second attempt taken while the first is still POSTing a
   * workspace-private conversation lists before that insert is visible, finds
   * nothing, and creates a second conversation.
   */
  const initializingRef = useRef(false);
  const submittingRef = useRef(false);
  const documentWaitDecisionPendingRef = useRef(false);
  const submissionGenerationRef = useRef(0);
  const groupSettingsGenerationRef = useRef(0);
  const backgroundCanvasSettingsGenerationRef = useRef(0);
  const backgroundCanvasSettingsWriteRef = useRef<Promise<void>>(
    Promise.resolve(),
  );
  const conversationProfileWriteRef = useRef<Promise<void>>(Promise.resolve());
  const solveResponseStyleWriteRef = useRef<Promise<void>>(Promise.resolve());
  const solveWebSearchWriteRef = useRef<Promise<void>>(Promise.resolve());
  const todayLocalDateRef = useRef<string | null>(null);
  scopeRef.current = scope;

  const clearAssistantDraft = useCallback((): void => {
    assistantAttemptEpochRef.current = 0;
    assistantDeltaSequenceRef.current = 0;
    setAssistantDraft("");
  }, []);

  /**
   * d30 — the ONE place Run state changes, and it only moves forward.
   *
   * Same Run: a terminal Run never goes back to active, `attempt_epoch` never
   * decreases, and `stop_requested_at` never clears once set. Another Run: an
   * older `created_at` never replaces a newer one. Only a read nothing
   * overtook reaches here (see {@link applyRunProjection}), so a read's `null`
   * is the server saying the Run is over, and it clears. A
   * different Run starts from an empty draft, progress and activity, so it
   * never shows the previous Run's text or inherits its attempt counters.
   * Answers whether the update was taken.
   */
  const updateRun = useCallback(
    (next: Run | null, source: RunUpdateSource = OBSERVED_RUN): boolean => {
      if (source.kind === "reset") {
        retiredRunRef.current = null;
      } else if (next === null) {
        if (source.kind !== "read") return false;
        if (runRef.current !== null) retiredRunRef.current = runRef.current;
      } else {
        if (source.kind === "observed") runObservationRef.current += 1;
        const admitted = admitRun(runRef.current, retiredRunRef.current, next);
        if (admitted === null) return false;
        next = admitted;
      }
      const changedRun =
        next !== null &&
        runRef.current !== null &&
        runRef.current.id !== next.id;
      runRef.current = next;
      setRun(next);
      if (
        next === null ||
        !ACTIVE_RUN_STATUSES.has(next.status) ||
        changedRun
      ) {
        clearAssistantDraft();
        setRunProgress(null);
        // A terminal Run clears transient activity even if a late event
        // arrives; the answer's own grounding stays in message history.
        activityAttemptEpochRef.current = 0;
        setRunActivities([]);
      }
      return true;
    },
    [clearAssistantDraft],
  );

  /**
   * Retire the activity projection when a newer attempt epoch is proven.
   *
   * Every visible row belongs to exactly one attempt, so a superseded
   * attempt's rows must disappear at the moment supersession is observed —
   * not whenever the new attempt first emits work of its own.
   */
  const advanceActivityEpoch = useCallback((attemptEpoch: number | null) => {
    if (
      attemptEpoch === null ||
      attemptEpoch <= activityAttemptEpochRef.current
    )
      return;
    activityAttemptEpochRef.current = attemptEpoch;
    setRunActivities([]);
  }, []);

  /**
   * d26 — the ONE Run projection a read applies: the initial load, Refresh,
   * window focus and the silence re-read alike, so they cannot drift apart.
   *
   * `observation` and `sequence` are taken when the read is issued (see
   * {@link beginRunRead}). A read that anything overtook — a later-issued read
   * already applied, or a live frame or reply observed since — changes
   * nothing: its snapshot is older than what the reader holds, and applying
   * it could move an active Run back (`waiting_documents` to `running`).
   * `heldRunId` is the Run this client held when it asked: a read's
   * `latest_failure` is shown only for THAT Run, so a refresh never raises an
   * older failure the reader had moved past. The initial load passes
   * `undefined` — it held nothing, and a reload must show the same card the
   * live frame did (AC6).
   *
   * Progress, activity and the draft follow only a Run the rule admitted; the
   * draft also keeps its own attempt and sequence fence, so a snapshot never
   * rewinds live text.
   */
  const applyRunProjection = useCallback(
    (hydration: ConversationHydration, read: RunRead): void => {
      // Overtaken reads change nothing — no Run, no card. A later-issued read
      // already applied, or a live frame / reply observed since this one was
      // issued: either way the reader already holds something newer.
      if (read.sequence <= appliedRunReadRef.current) return;
      appliedRunReadRef.current = read.sequence;
      if (read.observation !== runObservationRef.current) return;
      // Held when the read was issued AND still held now: a Send since then
      // has moved the reader on, and its own outcome must not be covered.
      const stillHeld =
        typeof read.heldRunId === "string" &&
        runRef.current?.id === read.heldRunId;
      const admitted = updateRun(hydration.active_run, READ_RUN);
      const latestFailure = hydration.latest_failure ?? null;
      if (
        latestFailure !== null &&
        (read.heldRunId === undefined ||
          (stillHeld && latestFailure.run_id === read.heldRunId))
      ) {
        setRunFailure(latestFailure);
        setFailure(terminalRunFailure(latestFailure, scopeRef.current));
      } else if (read.heldRunId === undefined) {
        setRunFailure(null);
      }
      if (!admitted) return;
      setRunProgress(hydration.active_progress ?? null);
      // A server without the activity contract leaves this empty and the
      // legacy progress line above stays the only signal.
      const hydratedActivities = (hydration.active_activities ?? []).flatMap(
        (candidate) => {
          const activity = toRunActivity(candidate);
          return activity ? [activity] : [];
        },
      );
      // Fence on the Run's own attempt, not on whichever rows arrived. An
      // attempt that has emitted nothing yet still supersedes its
      // predecessor, so seeding from the rows alone would leave the fence at
      // zero and let a delayed frame from the older attempt render as live
      // work. Fall back to the rows only when the Run cannot say (no active
      // Run, or a server that predates this field).
      const activeAttemptEpoch =
        eventNumber(hydration.active_run?.attempt_epoch) ??
        hydratedActivities.reduce(
          (epoch, activity) => Math.max(epoch, activity.attempt_epoch),
          0,
        );
      // Defensive: a projection row from another attempt is not current work
      // and must never reach the timeline.
      const current = hydratedActivities.filter(
        (activity) => activity.attempt_epoch === activeAttemptEpoch,
      );
      if (activeAttemptEpoch > activityAttemptEpochRef.current) {
        activityAttemptEpochRef.current = activeAttemptEpoch;
        setRunActivities(sortActivities(current));
      } else if (activeAttemptEpoch === activityAttemptEpochRef.current) {
        // The same attempt: merge, so a row a live frame already moved on is
        // never put back (upsertActivity never regresses one).
        setRunActivities((rows) => current.reduce(upsertActivity, rows));
      }
      const snapshot = hydration.assistant_snapshot;
      if (
        snapshot &&
        hydration.active_run?.id === snapshot.run_id &&
        runRef.current?.id === snapshot.run_id &&
        (snapshot.attempt_epoch > assistantAttemptEpochRef.current ||
          (snapshot.attempt_epoch === assistantAttemptEpochRef.current &&
            snapshot.delta_sequence >= assistantDeltaSequenceRef.current))
      ) {
        assistantAttemptEpochRef.current = snapshot.attempt_epoch;
        assistantDeltaSequenceRef.current = snapshot.delta_sequence;
        setAssistantDraft(snapshot.text);
      }
    },
    [updateRun],
  );

  /** Everything a Run read must take when it is ISSUED (d30, d26). */
  const beginRunRead = useCallback(
    (heldRunId: string | null | undefined): RunRead => ({
      observation: runObservationRef.current,
      sequence: ++runReadSequenceRef.current,
      heldRunId,
    }),
    [],
  );

  const refreshGroupSettings = useCallback(
    async (conversationId: string): Promise<void> => {
      const generation = ++groupSettingsGenerationRef.current;
      try {
        const envelope = await client.getGroupSettings(conversationId);
        if (
          generation !== groupSettingsGenerationRef.current ||
          activeConversationRef.current !== conversationId
        ) {
          return;
        }
        setGroupSettings(envelope.group_settings);
      } catch (readError) {
        if (
          generation !== groupSettingsGenerationRef.current ||
          activeConversationRef.current !== conversationId
        ) {
          return;
        }
        // A SOLVE that dropped back to one AI contributor answers 409. That is
        // the mode transition, not a failure: hide the group affordances and
        // say nothing (AC3).
        if (
          readError instanceof PlayerAIProblem &&
          readError.problem.code === "GROUP_MODE_INACTIVE"
        ) {
          setGroupSettings(null);
          return;
        }
        setFailure(requestFailure(readError, scopeRef.current));
      }
    },
    [client],
  );

  const refreshPlatformActionBatch = useCallback(
    async (conversationId: string): Promise<void> => {
      const currentScope = scopeRef.current;
      if (
        currentScope?.conversationType !== "private" ||
        "workspaceType" in currentScope
      ) {
        setPlatformActionBatch(null);
        return;
      }
      try {
        const batch =
          await client.getCurrentPlatformActionBatch(conversationId);
        if (activeConversationRef.current === conversationId) {
          setPlatformActionBatch(batch);
        }
      } catch (readError) {
        if (activeConversationRef.current === conversationId) {
          /*
           * A terminal Run failure OUTRANKS this read, and the ordering is not
           * hypothetical: this refresh is kicked off BY `run.failed`, so a
           * conversation whose Run failed while this ancillary read was also
           * unavailable replaced "Player AI couldn't complete that response"
           * with "Player AI couldn't load that" — the reader lost both the
           * explanation and the retry, for a read they never asked for.
           *
           * Only a `response`-stage failure wins; anything else is replaced as
           * before, so a real request failure still surfaces.
           */
          setFailure((current) =>
            current?.diagnostics.stage === "response"
              ? current
              : requestFailure(readError, scopeRef.current),
          );
        }
      }
    },
    [client],
  );

  /**
   * Replace a live failure projection with the SERVER's (PB-5276).
   *
   * The `run.failed` frame cannot say whether re-asking would duplicate work:
   * that depends on this Run's Proposals in every state and on the
   * conversation's cross-app batches, none of which the browser authoritatively
   * holds. So the live card is built without `retry_safe` — withholding the
   * retry — and this one read upgrades it, giving the live card exactly what a
   * reloaded one gets.
   *
   * Fenced three ways, because a terminal failure is precisely when a reader
   * switches away: the conversation must still be active, the load generation
   * must not have moved, and the failure on screen must still be the Run this
   * read was issued for. Failure is silent — the card is already correct and
   * merely conservative.
   */
  const reconcileRunFailure = useCallback(
    async (conversationId: string, runId: string): Promise<void> => {
      const generation = loadGenerationRef.current;
      try {
        const hydration = await client.hydrateConversation(conversationId);
        if (
          generation !== loadGenerationRef.current ||
          activeConversationRef.current !== conversationId ||
          hydration.latest_failure?.run_id !== runId
        ) {
          return;
        }
        const confirmed = hydration.latest_failure;
        setRunFailure((current) =>
          current?.run_id === runId ? confirmed : current,
        );
        setFailure((current) =>
          current?.diagnostics.runId === runId
            ? terminalRunFailure(confirmed, scopeRef.current)
            : current,
        );
      } catch {
        // Leave the conservative card exactly where it is.
      }
    },
    [client],
  );

  const applyEvent = useCallback(
    (event: PlayerAIEvent): void => {
      if (event.id !== null) {
        setEventCursor((current) => Math.max(current, event.id ?? current));
      }
      const { data, eventType } = event;
      if (eventType === "typing.updated") {
        // Ephemeral and whole-state: each frame replaces the last (Task 15).
        typingFrameRef.current += 1;
        setTypingParticipants(toTypingParticipants(data.typing));
        return;
      }
      if (eventType === "assistant.delta") {
        const delta = data.delta;
        const attemptEpoch = eventNumber(data.attempt_epoch);
        const deltaSequence = eventNumber(data.delta_sequence);
        if (
          typeof delta !== "string" ||
          attemptEpoch === null ||
          deltaSequence === null ||
          runRef.current === null ||
          !STREAMING_RUN_STATUSES.has(runRef.current.status)
        ) {
          return;
        }
        if (attemptEpoch < assistantAttemptEpochRef.current) return;
        if (attemptEpoch > assistantAttemptEpochRef.current) {
          assistantAttemptEpochRef.current = attemptEpoch;
          assistantDeltaSequenceRef.current = 0;
          setAssistantDraft("");
          setRunProgress(null);
        }
        if (
          deltaSequence <= assistantDeltaSequenceRef.current ||
          deltaSequence !== assistantDeltaSequenceRef.current + 1
        ) {
          return;
        }
        assistantDeltaSequenceRef.current = deltaSequence;
        setAssistantDraft((value) => value + delta);
        return;
      }
      if (eventType === "assistant.snapshot") {
        const text = data.text;
        const attemptEpoch = eventNumber(data.attempt_epoch);
        const deltaSequence = eventNumber(data.delta_sequence);
        if (
          typeof text !== "string" ||
          attemptEpoch === null ||
          deltaSequence === null ||
          attemptEpoch < assistantAttemptEpochRef.current ||
          (attemptEpoch === assistantAttemptEpochRef.current &&
            deltaSequence < assistantDeltaSequenceRef.current)
        ) {
          return;
        }
        assistantAttemptEpochRef.current = attemptEpoch;
        assistantDeltaSequenceRef.current = deltaSequence;
        setAssistantDraft(text);
        return;
      }
      if (eventType === "run.output_reset") {
        const attemptEpoch = eventNumber(data.attempt_epoch);
        if (
          attemptEpoch !== null &&
          attemptEpoch > assistantAttemptEpochRef.current
        ) {
          assistantAttemptEpochRef.current = attemptEpoch;
          assistantDeltaSequenceRef.current = 0;
          setAssistantDraft("");
          setRunProgress(null);
        }
        // start_attempt emits run.output_reset before the resumed run.started,
        // so this is the first frame that proves the previous attempt was
        // superseded. Drop its rows here or they keep spinning until the new
        // attempt happens to emit an activity.
        advanceActivityEpoch(attemptEpoch);
        return;
      }
      if (eventType === "run.progress") {
        const stage = data.stage;
        const startedAt = data.occurred_at;
        if (
          (stage === "reviewing_canvas" ||
            stage === "preparing_canvas_proposal") &&
          typeof startedAt === "string"
        ) {
          setRunProgress({ stage, started_at: startedAt });
        } else {
          setRunProgress(null);
        }
        return;
      }
      if (eventType === "run.activity") {
        const activity = toRunActivity(data);
        if (
          activity === null ||
          runRef.current === null ||
          !ACTIVE_RUN_STATUSES.has(runRef.current.status)
        ) {
          return;
        }
        // Fence on the attempt epoch: a superseded attempt can never update
        // the visible list, and a newer attempt replaces it wholesale.
        if (activity.attempt_epoch < activityAttemptEpochRef.current) return;
        advanceActivityEpoch(activity.attempt_epoch);
        setRunActivities((current) => upsertActivity(current, activity));
        return;
      }
      if (eventType === "message.created") {
        const message = data.message;
        if (isMessage(message)) {
          setMessages((current) => upsertMessage(current, message));
          if (message.role === "assistant") clearAssistantDraft();
          return;
        }
      }
      if (eventType.startsWith("run.") && isRun(data.run)) {
        // A resumed run.started carries the new epoch; every other durable
        // frame carries 0, which can never advance the fence.
        advanceActivityEpoch(eventNumber(data.attempt_epoch));
        if (eventType === "run.failed") {
          const reported = data.error;
          const code =
            typeof reported === "object" &&
            reported !== null &&
            typeof (reported as Record<string, unknown>).code === "string"
              ? String((reported as Record<string, unknown>).code)
              : "RUNTIME_FAILURE";
          /*
           * The SAME projection the hydration path builds, so the live card and
           * the reloaded one are produced by one function from one shape.
           *
           * `retry_safe` is deliberately ABSENT here. Whether a second ask
           * could duplicate work is a fact about rows this client does not
           * hold — its Proposal list is a page, and a detached or group card
           * may not be in it at all — so the live card withholds the retry and
           * `reconcileRunFailure` below replaces this projection with the
           * server's as soon as it lands.
           */
          const failed = data.run;
          const durable: RunFailure = {
            run_id: failed.id,
            error_code: code,
            occurred_at: new Date().toISOString(),
            attempt_epoch: failed.attempt_epoch,
            outcome: failed.outcome,
            runtime_release: failed.runtime_release,
            trigger_type: failed.trigger_type,
            trigger_message_id: failed.trigger_message_id ?? null,
            requested_by_user_id: failed.requested_by_user_id,
          };
          setRunFailure(durable);
          setFailure(terminalRunFailure(durable, scopeRef.current));
          const failedIn = activeConversationRef.current;
          if (failedIn) void reconcileRunFailure(failedIn, failed.id);
        } else if (
          eventType === "run.started" ||
          eventType === "run.completed"
        ) {
          setRunFailure(null);
          setFailure(null);
        }
        updateRun(data.run);
        if (eventType === "run.completed" || eventType === "run.failed") {
          const conversationId = activeConversationRef.current;
          if (conversationId) void refreshPlatformActionBatch(conversationId);
        }
        return;
      }
      if (eventType.startsWith("proposal.")) {
        const proposal = data.proposal;
        if (isProposal(proposal)) {
          proposalRevisionsRef.current.set(
            proposal.id,
            (proposalRevisionsRef.current.get(proposal.id) ?? 0) + 1,
          );
          setProposals((current) => upsertProposal(current, proposal));
          return;
        }
      }
      if (
        eventType === "context.compacted" ||
        eventType === "context.compaction_failed"
      ) {
        /* The DURABLE half of the compaction experience. The transient status
           already rides the `compacting_context` run activity; this is the
           small thread notice that survives the Run — "Context compacted, you
           can continue here", or "Couldn't compact context" with a Retry.

           Content-free by contract: counts and a safe failure code. The
           checkpoint body is private conversation content and is not on the
           wire. Last-one-wins rather than a list, because the notice describes
           the thread's CURRENT context state, not its history. */
        setContextNotice({
          status: eventType === "context.compacted" ? "compacted" : "failed",
          covered_message_count:
            typeof data.covered_message_count === "number"
              ? data.covered_message_count
              : 0,
          failure_code:
            typeof data.failure_code === "string" ? data.failure_code : null,
          occurred_at:
            typeof data.occurred_at === "string" ? data.occurred_at : null,
        });
        return;
      }
      if (eventType === "specialist.review.failed") {
        // Restrained separate status for a terminally failed background canvas
        // review: never a chat message, never assistant narration.
        const specialistJobId = data.specialist_job_id;
        if (
          typeof specialistJobId !== "string" ||
          specialistJobId.length === 0
        ) {
          return;
        }
        setBackgroundReviewFailure({
          specialist_job_id: specialistJobId,
          error_code:
            typeof data.error_code === "string" ? data.error_code : null,
          occurred_at:
            typeof data.occurred_at === "string" ? data.occurred_at : null,
        });
        return;
      }
      if (eventType.startsWith("group.")) {
        // Content-free by contract, so the event says something changed and
        // the projection is re-read rather than reconstructed from the payload.
        const conversationId = activeConversationRef.current;
        if (conversationId) void refreshGroupSettings(conversationId);
      }
    },
    [
      advanceActivityEpoch,
      clearAssistantDraft,
      refreshGroupSettings,
      reconcileRunFailure,
      refreshPlatformActionBatch,
      updateRun,
    ],
  );

  /**
   * "Try again" on a failed compaction.
   *
   * Replaces the failure with the confirmed result, only in the conversation
   * that requested it. `not_needed` and `nothing_eligible` clear the notice:
   * there is nothing left for the reader to act on.
   */
  const retryContextCompaction = useCallback(async (): Promise<void> => {
    const conversationId = activeConversationRef.current;
    if (!conversationId) return;
    try {
      const result = await client.compactConversationContext(conversationId);
      if (activeConversationRef.current !== conversationId) return;
      setContextNotice(
        result.status === "failed" || result.status === "compacted"
          ? {
              status: result.status,
              covered_message_count: result.covered_message_count,
              failure_code: null,
              occurred_at: null,
            }
          : null,
      );
    } catch {
      // The previous checkpoint still stands and the thread still works, so
      // the notice stays exactly as it was rather than escalating.
    }
  }, [client]);

  /** Who is typing now (Task 15); a failure leaves the indicator as it was. */
  const refreshTyping = useCallback(
    async (conversationId: string): Promise<void> => {
      const frame = typingFrameRef.current;
      const generation = loadGenerationRef.current;
      try {
        const envelope = await client.getTyping(conversationId);
        // A frame that arrived during the read is newer than the read.
        if (
          activeConversationRef.current === conversationId &&
          loadGenerationRef.current === generation &&
          typingFrameRef.current === frame
        ) {
          setTypingParticipants(toTypingParticipants(envelope.typing));
        }
      } catch {
        // Typing is a hint; the next frame or expiry corrects it.
      }
    },
    [client],
  );

  const selectConversation = useCallback(
    async (conversationId: string): Promise<boolean> => {
      const generation = ++loadGenerationRef.current;
      selectFailureRef.current = null;
      ++refreshGenerationRef.current;
      disconnectRef.current?.();
      disconnectRef.current = null;
      ++submissionGenerationRef.current;
      submittingRef.current = false;
      setIsSubmittingMessage(false);
      activeConversationRef.current = conversationId;
      setActiveConversationId(conversationId);
      setConnectionState("idle");
      setStreamReconnectAttempt(0);
      setIsConversationLoading(true);
      setFailure(null);
      setMessages([]);
      proposalRevisionsRef.current.clear();
      setProposals([]);
      setPlatformActionBatch(null);
      setRunProgress(null);
      setRunActivities([]);
      setRunFailure(null);
      updateRun(null, RESET_RUN);
      transportRestartPendingRef.current = false;
      setCapabilities(null);
      setGroupSettings(null);
      setBackgroundCanvasSettings(null);
      setConversationProfileState(null);
      backgroundCanvasSettingsRef.current = null;
      conversationProfileRef.current = null;
      setBackgroundReviewFailure(null);
      // The notice describes ONE thread's context, so switching threads must
      // clear it — otherwise a failure notice follows the reader into a
      // conversation it says nothing about.
      setContextNotice(null);
      setTypingParticipants([]);
      ++groupSettingsGenerationRef.current;
      ++backgroundCanvasSettingsGenerationRef.current;
      setEventCursor(0);
      beforeSequenceRef.current = null;
      setHasMoreMessages(false);
      setIsLoadingMessages(false);
      // Sampled BEFORE the read, for the same reason as in `refreshConversation`.
      const styleWriteGeneration = solveResponseStyleWriteGenerationRef.current;
      const webSearchWriteGeneration = solveWebSearchWriteGenerationRef.current;
      const runRead = beginRunRead(undefined);
      try {
        const hydration = await client.hydrateConversation(conversationId);
        if (
          generation !== loadGenerationRef.current ||
          activeConversationRef.current !== conversationId
        )
          return false;
        setCapabilities(hydration.capabilities);
        setAuthorityStatus("valid");
        const proposalRevisions = new Map(proposalRevisionsRef.current);
        const [messagePage, proposalPage, profileConfiguration] =
          await Promise.all([
            client
              .fetchMessages(conversationId, { limit: 50 })
              .catch((error) => {
                if (generation === loadGenerationRef.current)
                  setFailure(requestFailure(error, scopeRef.current));
                return { messages: [], next_before_sequence: null };
              }),
            client
              .listProposals(conversationId, { limit: 50 })
              .catch((error) => {
                if (generation === loadGenerationRef.current)
                  setFailure(requestFailure(error, scopeRef.current));
                return { proposals: [] };
              }),
            client.getConversationProfile(conversationId).catch(() => null),
          ]);
        if (
          generation !== loadGenerationRef.current ||
          activeConversationRef.current !== conversationId
        ) {
          return false;
        }
        setConversations((current) =>
          upsertConversation(current, hydration.conversation),
        );
        // MERGE, never replace. `refreshConversation` can complete while this
        // first page is still in flight — it fences on its own generation, not
        // on this one — and its hydration is what CREATES the durable system
        // messages this page was fetched too early to contain. Replacing here
        // dropped exactly those. Merging is safe because `setMessages([])`
        // above already cleared the previous conversation and every writer
        // since is guarded on this same `conversationId`, so nothing stale can
        // survive; the page still wins for any id it carries.
        setMessages((current) => mergeMessages(current, messagePage.messages));
        beforeSequenceRef.current = messagePage.next_before_sequence;
        setHasMoreMessages(messagePage.next_before_sequence !== null);
        mergeProposalSnapshot(proposalPage.proposals, proposalRevisions);
        // AC6: a reloaded failure card is built by the same function from the
        // same shape as the live one, so the two cannot drift apart.
        applyRunProjection(hydration, runRead);
        clearSolveResponseStyleOverride(conversationId, styleWriteGeneration);
        clearSolveWebSearchOverride(conversationId, webSearchWriteGeneration);
        // Absent from a server that predates group collaboration, and null in
        // every individual conversation — both read as "no group affordances".
        setGroupSettings(hydration.group_settings ?? null);
        setConversationProfileState(profileConfiguration);
        conversationProfileRef.current = profileConfiguration;
        setPlatformActionBatch(hydration.pending_platform_action_batch ?? null);
        setEventCursor(hydration.event_cursor);
        setIsConversationLoading(false);
        // From the group settings the hydration already carries: only a
        // group conversation has typing to recover (Task 15).
        const sharedConversation = hydration.group_settings?.mode === "group";
        transportActivityAtRef.current = Date.now();
        disconnectRef.current = client.connectEvents(
          conversationId,
          hydration.event_cursor,
          {
            onEvent: (event) => {
              if (activeConversationRef.current === conversationId) {
                applyEvent(event);
              }
            },
            onState: (state) => {
              setConnectionState(state);
              // Typing frames missed while disconnected are not replayed:
              // every (re)connect starts from the current state (Task 15).
              if (state === "open" && sharedConversation) {
                void refreshTyping(conversationId);
              }
            },
            onReconnectAttempt: setStreamReconnectAttempt,
            onActivity: () => {
              transportActivityAtRef.current = Date.now();
            },
            // Only an expired session is the reader's to act on; every other
            // transport error is retried, or ended as an access question.
            onError: (streamError) => {
              if (activeConversationRef.current !== conversationId) return;
              const streamFailure = requestFailure(
                streamError,
                scopeRef.current,
              );
              if (streamFailure.family === "session_expired") {
                setFailure(streamFailure);
              }
            },
            // d25: a 403 or 404 ended both transports. Re-check access; the
            // refresh restarts the transport once if it is confirmed, and a
            // denial leaves it stopped behind the denied notice.
            onAccessQuestion: () => {
              if (activeConversationRef.current !== conversationId) return;
              transportRestartPendingRef.current = true;
              void refreshConversationRef.current(true);
            },
          },
        );
        return true;
      } catch (loadError) {
        if (generation !== loadGenerationRef.current) return false;
        setConnectionState("closed");
        setIsConversationLoading(false);
        const startupFailure = requestFailure(loadError, scopeRef.current);
        // Recorded as well as surfaced: initialization reads this to tell a
        // real startup failure from a superseded load, which also answers
        // `false` but leaves this null.
        selectFailureRef.current = startupFailure;
        setFailure(startupFailure);
        return false;
      }
    },
    [
      applyEvent,
      applyRunProjection,
      clearSolveResponseStyleOverride,
      clearSolveWebSearchOverride,
      client,
      mergeProposalSnapshot,
      refreshTyping,
      updateRun,
    ],
  );

  const refreshConversation = useCallback(
    async function refresh(
      permissionsOnly = false,
      attempt = 0,
      submissionOwner?: number,
    ): Promise<void> {
      let conversationId = activeConversationRef.current;
      if (conversationId === null) return;
      const loadGeneration = loadGenerationRef.current;
      const refreshGeneration = ++refreshGenerationRef.current;
      // `checking`, NOT a mask. The last confirmed capabilities stay in force
      // for the whole of a healthy check — see `PlayerAIAuthorityStatus`.
      setAuthorityStatus((current) =>
        current === "denied" ? current : "checking",
      );
      const profileBefore = conversationProfileRef.current;
      const groupGeneration = groupSettingsGenerationRef.current;
      const backgroundBefore = backgroundCanvasSettingsRef.current;
      // Sampled BEFORE the read, so a write confirmed while it is in flight is
      // detectable when the response lands.
      const styleWriteGeneration = solveResponseStyleWriteGenerationRef.current;
      const webSearchWriteGeneration = solveWebSearchWriteGenerationRef.current;
      const runRead = beginRunRead(runRef.current?.id ?? null);
      const heldActiveRun =
        runRef.current !== null &&
        ACTIVE_RUN_STATUSES.has(runRef.current.status);
      // A Send made after this refresh began owns the notice it sets: this
      // refresh — its retries included — must not clear or replace it, or a
      // late reply could hide a newer refusal or show an old failure after a
      // newer success. A confirmed access denial still shows.
      const submissionBefore =
        submissionOwner ?? submissionGenerationRef.current;
      const ownsNotice = (): boolean =>
        submissionGenerationRef.current === submissionBefore;
      let readTranscript = !permissionsOnly;
      try {
        const currentScope = scopeRef.current;
        if (
          currentScope &&
          "workspaceId" in currentScope &&
          currentScope.conversationType !== "workspace" &&
          hasSingletonPrivateConversation(currentScope.workspaceType)
        ) {
          const active = await client.resolveActiveConversation(
            currentScope.workspaceType,
            currentScope.workspaceId,
          );
          if (
            loadGeneration !== loadGenerationRef.current ||
            refreshGeneration !== refreshGenerationRef.current
          ) {
            return;
          }
          conversationSurfaceRef.current = active.conversation_mode;
          setConversationSurface(active.conversation_mode);
          setIsEffectiveDriver(active.is_effective_driver === true);
          if (active.conversation.id !== conversationId) {
            setConversations((current) =>
              upsertConversation(current, active.conversation, true),
            );
            await selectConversation(active.conversation.id);
            return;
          }
          conversationId = active.conversation.id;
        }
        // Hydration performs the server-owned workspace reconciliation. The
        // follow-up message read recovers when this tab did not receive the
        // resulting `message.created` frame from the live stream.
        /*
         * Hydration is the AUTHORITATIVE read, and it is awaited alone.
         *
         * The profile and specialist-settings reads used to travel with it in
         * one `Promise.all` inside this try, so a slow or failing MODEL-PROFILE
         * read marked the caller's ACCESS unavailable — a settings outage
         * presented as a loss of permission. They are read below, outside the
         * authority decision, exactly as the initial load already tolerates
         * them failing.
         */
        const hydration = await client.hydrateConversation(conversationId);
        if (
          loadGeneration !== loadGenerationRef.current ||
          refreshGeneration !== refreshGenerationRef.current ||
          activeConversationRef.current !== conversationId
        ) {
          return;
        }
        setConversations((current) =>
          upsertConversation(current, hydration.conversation),
        );
        setCapabilities(hydration.capabilities);
        setAuthorityStatus("valid");
        if (ownsNotice()) setFailure(null);
        // d26: Refresh and focus apply the same Run projection as the load,
        // so a Run that finished behind a silent line unlocks here too.
        applyRunProjection(hydration, runRead);
        // An access-only check that retires a busy Run also takes away the
        // silence re-read, which was the only thing still going to fetch its
        // answer — so this check reads the transcript too.
        if (
          heldActiveRun &&
          (runRef.current === null ||
            !ACTIVE_RUN_STATUSES.has(runRef.current.status))
        ) {
          readTranscript = true;
        }
        // d25: access is confirmed, so a transport a 403/404 ended restarts —
        // once, live line first. The client refuses a second restart.
        if (transportRestartPendingRef.current) {
          transportRestartPendingRef.current = false;
          disconnectRef.current?.restart?.();
        }
        // main's profile/background restore is NOT repeated here: those reads
        // moved out of this try block so a settings outage can no longer be
        // read as a loss of access, and the same restore runs below where they
        // actually resolve.

        clearSolveResponseStyleOverride(conversationId, styleWriteGeneration);
        clearSolveWebSearchOverride(conversationId, webSearchWriteGeneration);
        if (groupGeneration === groupSettingsGenerationRef.current)
          setGroupSettings(hydration.group_settings ?? null);
        setPlatformActionBatch(
          (current) =>
            hydration.pending_platform_action_batch ??
            (current && current.status !== "pending_confirmation"
              ? current
              : null),
        );
        if (!permissionsOnly)
          setEventCursor((current) =>
            Math.max(current, hydration.event_cursor),
          );
      } catch (refreshError) {
        if (
          loadGeneration !== loadGenerationRef.current ||
          refreshGeneration !== refreshGenerationRef.current ||
          activeConversationRef.current !== conversationId
        ) {
          return;
        }
        if (
          refreshError instanceof PlayerAIProblem &&
          [403, 404].includes(refreshError.problem.status ?? 0)
        ) {
          // A CONFIRMED denial: apply the returned restrictions and stay there.
          // An ended transport stays ended (d25).
          transportRestartPendingRef.current = false;
          setCapabilities(null);
          setGroupSettings(null);
          setAuthorityStatus("denied");
          // The server's OWN answer decides the family: a 403 and a 404 both
          // reach `permission_denied`, and the code is read from the envelope
          // rather than assumed, so a future refusal code lands on its own
          // family instead of on this one's copy.
          setFailure(requestFailure(refreshError, scopeRef.current));
        } else {
          // The check could not be completed. Fail closed — but as
          // "unavailable, retry" rather than as a denial nobody issued.
          setAuthorityStatus("unavailable");
          if (ownsNotice())
            setFailure(requestFailure(refreshError, scopeRef.current));
          // main's bounded automatic retry, kept: a transient transport
          // failure recovers without the reader doing anything, and a
          // succeeding attempt promotes the authority back to `valid` through
          // its own completed hydration. Stale authority is still never
          // restored here — `unavailable` holds until a read actually lands.
          if (attempt < 2) {
            await new Promise<void>((resolve) =>
              setTimeout(resolve, 1000 * (attempt + 1)),
            );
            if (
              loadGeneration !== loadGenerationRef.current ||
              refreshGeneration !== refreshGenerationRef.current ||
              activeConversationRef.current !== conversationId
            )
              return;
            await refresh(permissionsOnly, attempt + 1, submissionBefore);
          }
        }
        return;
      }

      /*
       * Settings and transcript, read AFTER authority is settled and outside
       * its try. A failure here surfaces as an error and leaves access exactly
       * where the authoritative read left it.
       */
      try {
        const proposalRevisions = new Map(proposalRevisionsRef.current);
        const [
          messagePage,
          proposalPage,
          profileConfiguration,
          backgroundConfiguration,
        ] = await Promise.all([
          readTranscript
            ? client.fetchMessages(conversationId, { limit: 50 })
            : Promise.resolve(null),
          readTranscript
            ? client.listProposals(conversationId, { limit: 50 })
            : Promise.resolve(null),
          client.getConversationProfile(conversationId),
          backgroundBefore
            ? client.getConversationSpecialistSettings(
                conversationId,
                SOLVE_CANVAS_SPECIALIST_ID,
              )
            : Promise.resolve(null),
        ]);
        if (
          loadGeneration !== loadGenerationRef.current ||
          refreshGeneration !== refreshGenerationRef.current ||
          activeConversationRef.current !== conversationId
        ) {
          return;
        }
        if (messagePage)
          setMessages((current) =>
            mergeMessages(current, messagePage.messages),
          );
        if (proposalPage)
          mergeProposalSnapshot(proposalPage.proposals, proposalRevisions);
        if (conversationProfileRef.current === profileBefore) {
          conversationProfileRef.current = profileConfiguration;
          setConversationProfileState(profileConfiguration);
        }
        if (
          backgroundCanvasSettingsRef.current === backgroundBefore &&
          backgroundConfiguration
        ) {
          backgroundCanvasSettingsRef.current = backgroundConfiguration;
          setBackgroundCanvasSettings(backgroundConfiguration);
        }
      } catch (settingsError) {
        if (
          loadGeneration !== loadGenerationRef.current ||
          refreshGeneration !== refreshGenerationRef.current ||
          activeConversationRef.current !== conversationId
        ) {
          return;
        }
        if (ownsNotice())
          setFailure(requestFailure(settingsError, scopeRef.current));
      }
    },
    [
      applyRunProjection,
      beginRunRead,
      clearSolveResponseStyleOverride,
      clearSolveWebSearchOverride,
      client,
      mergeProposalSnapshot,
      selectConversation,
    ],
  );
  refreshConversationRef.current = refreshConversation;

  /**
   * d26 — the safety re-read behind a silent transport.
   *
   * A filter can hold the live line without a single error, so a Run can end
   * while this client hears nothing. This re-reads the Run, the transcript and
   * the Proposals, and applies the shared projection: a finished Run unlocks,
   * a failed one shows its card, an approval appears. Deliberately NOT
   * `refreshConversation`: a background check must never move the reader's
   * authority status, and a failure here is silent — the next pass tries
   * again, exactly like the heartbeat it stands in for.
   */
  const rereadSilentRun = useCallback(
    async (conversationId: string): Promise<void> => {
      const generation = loadGenerationRef.current;
      const runRead = beginRunRead(runRef.current?.id ?? null);
      const proposalRevisions = new Map(proposalRevisionsRef.current);
      try {
        const [hydration, messagePage, proposalPage] = await Promise.all([
          client.hydrateConversation(conversationId),
          client.fetchMessages(conversationId, { limit: 50 }),
          client.listProposals(conversationId, { limit: 50 }),
        ]);
        if (
          generation !== loadGenerationRef.current ||
          activeConversationRef.current !== conversationId
        ) {
          return;
        }
        setMessages((current) => mergeMessages(current, messagePage.messages));
        mergeProposalSnapshot(proposalPage.proposals, proposalRevisions);
        applyRunProjection(hydration, runRead);
        setEventCursor((current) => Math.max(current, hydration.event_cursor));
      } catch {
        // The Run stays as it was; the next silent stretch asks again.
      }
    },
    [applyRunProjection, beginRunRead, client, mergeProposalSnapshot],
  );

  const isRunActive = run !== null && ACTIVE_RUN_STATUSES.has(run.status);
  useEffect(() => {
    if (!isRunActive || activeConversationId === null) return;
    // Silence is measured from the later of the last sign of life and the
    // moment this Run became active, so a Send on a long-quiet line does not
    // trigger a read at once.
    let since = Date.now();
    let timer: ReturnType<typeof setTimeout>;
    const check = (): void => {
      const quietFor =
        Date.now() - Math.max(since, transportActivityAtRef.current);
      if (quietFor >= SILENCE_REREAD_MS) {
        since = Date.now();
        void rereadSilentRun(activeConversationId);
        timer = setTimeout(check, SILENCE_REREAD_MS);
        return;
      }
      timer = setTimeout(check, SILENCE_REREAD_MS - quietFor);
    };
    timer = setTimeout(check, SILENCE_REREAD_MS);
    return () => clearTimeout(timer);
  }, [activeConversationId, isRunActive, rereadSilentRun]);

  const createConversation = useCallback(async (): Promise<boolean> => {
    const currentScope = scopeRef.current;
    if (currentScope?.conversationType !== "private") return false;
    // A singleton scope has exactly one durable conversation per participant
    // (D-018), so "new conversation" has no meaning there and the server would
    // refuse it. Returning quietly rather than surfacing an error: the rail
    // simply does not offer the affordance for these scopes.
    if (
      "workspaceId" in currentScope &&
      hasSingletonPrivateConversation(currentScope.workspaceType)
    ) {
      return false;
    }
    try {
      const conversation =
        "workspaceId" in currentScope
          ? await client.createWorkspacePrivateConversation(
              currentScope.workspaceType,
              currentScope.workspaceId,
            )
          : await client.createPrivateConversation();
      setConversations((current) =>
        upsertConversation(current, conversation, true),
      );
      return await selectConversation(conversation.id);
    } catch (creationError) {
      setFailure(requestFailure(creationError, scopeRef.current, "write"));
      return false;
    }
  }, [client, selectConversation]);

  const sendMessage = useCallback(
    async (
      text: string,
      interactionContext?: InteractionContext,
      options?: {
        readonly startRun?: boolean;
        readonly attachmentIds?: readonly string[];
        readonly mentionsPlayerAI?: boolean;
        readonly playerAIMentionOccurrenceIndices?: readonly number[];
        readonly personMentions?: readonly {
          readonly userId: string;
          readonly token: string;
          readonly occurrenceIndices: readonly number[];
        }[];
        readonly replyToMessageId?: string;
        readonly command?: "next_phase";
        readonly onAccepted?: () => void;
      },
    ): Promise<boolean> => {
      const conversationId = activeConversationRef.current;
      const currentScope = scopeRef.current;
      const trimmed = text.trim();
      if (
        !conversationId ||
        !currentScope ||
        !trimmed ||
        submittingRef.current
      ) {
        return false;
      }
      /*
       * PB-5380 — the host's composer stops at the limit, so this is the
       * backstop for a race or an older host. It THROWS, never returns false:
       * a send handler that resolves makes the rail clear its draft, and the
       * person would lose the message and its files over a refusal. Outside the
       * try, so the refusal keeps its own reason rather than being re-described.
       */
      if ((options?.attachmentIds?.length ?? 0) > MAX_MESSAGE_ATTACHMENTS) {
        const refused = describePlayerAIFailure({
          source: "write",
          code: "VALIDATION_FAILED",
          attachmentLimit: MAX_MESSAGE_ATTACHMENTS,
          surface: scopeSurface(currentScope),
        });
        setFailure(refused);
        throw new CarriedPlayerAIFailure(refused);
      }
      const submissionGeneration = ++submissionGenerationRef.current;
      submittingRef.current = true;
      setIsSubmittingMessage(true);
      let notice: PlayerAIFailure | null = null;
      try {
        const serverRoutedToGroup = conversationSurfaceRef.current === "group";
        if (
          !serverRoutedToGroup &&
          (currentScope.conversationType === "private" ||
            options?.startRun === true)
        ) {
          const result = await client.createMessageRun(conversationId, {
            clientActionId: randomUuid(),
            clientMessageId: randomUuid(),
            text: trimmed,
            currentDate: browserCurrentDate(),
            ...(interactionContext ? { interactionContext } : {}),
            ...(options?.attachmentIds?.length
              ? { attachmentIds: options.attachmentIds }
              : {}),
            ...(options?.replyToMessageId
              ? { replyToMessageId: options.replyToMessageId }
              : {}),
          });
          // Taken by the server, whatever the chat on screen is now.
          options?.onAccepted?.();
          if (activeConversationRef.current !== conversationId) return false;
          if (result.message) {
            const message = result.message;
            setMessages((current) => upsertMessage(current, message));
          }
          clearAssistantDraft();
          updateRun(result.run);
          setEventCursor((current) => Math.max(current, result.event_cursor));
          // d3's send check: the reply's cursor must reach the live line.
          disconnectRef.current?.expectEvent?.(result.event_cursor);
        } else {
          // Structured metadata is the explicit trigger for autocomplete
          // mentions. The server also recognizes the portable `[Player AI]`
          // token and refuses unauthorized mentions in either form. A person
          // mention (Task 6) addresses them: it notifies nobody, and a message
          // naming ONLY people is not a Player AI request.
          const mentions: MessageMention[] = [
            ...(options?.mentionsPlayerAI
              ? [
                  {
                    type: "player_ai" as const,
                    occurrence_indices: [
                      ...(options.playerAIMentionOccurrenceIndices ?? []),
                    ],
                  },
                ]
              : []),
            ...(options?.personMentions ?? []).map((person) => ({
              type: "user" as const,
              user_id: person.userId,
              token: person.token,
              occurrence_indices: [...person.occurrenceIndices],
            })),
          ];
          const result = await client.postSharedMessage(
            conversationId,
            randomUuid(),
            trimmed,
            interactionContext,
            options?.attachmentIds ?? [],
            mentions,
            options?.command ? [{ type: options.command }] : [],
            options?.replyToMessageId ?? null,
          );
          options?.onAccepted?.();
          if (activeConversationRef.current !== conversationId) return false;
          setMessages((current) => upsertMessage(current, result.message));
          // d8: the busy signal comes from the reply itself, so it shows even
          // when no live frame ever reaches this browser.
          if (result.run) updateRun(result.run);
          setEventCursor((current) => Math.max(current, result.event_cursor));
          disconnectRef.current?.expectEvent?.(result.event_cursor);
          if (result.run_deferred === "capacity") {
            notice = describePlayerAIFailure({
              source: "write",
              code: RUN_DEFERRED_CODE,
              surface: scopeSurface(currentScope),
            });
          }
        }
        setFailure(notice);
        setRunFailure(null);
        return true;
      } catch (sendError) {
        if (activeConversationRef.current === conversationId) {
          const sendFailure = requestFailure(
            sendError,
            scopeRef.current,
            "write",
          );
          setFailure(sendFailure);
          /* A send that RACED a real revocation. The server is the gate and it
             has just refused, so correct the UI from the authoritative read
             rather than leaving the composer open on access that is gone. The
             message is NOT replayed — the user decides what to do next. */
          if (
            sendError instanceof PlayerAIProblem &&
            sendError.problem.status === 403
          ) {
            void refreshConversation(true);
          } else if (isPlayerAIRequestTimeout(sendError)) {
            /* n9: a Send that never answered may or may not have landed. Re-read
               so the transcript shows which, and keep the card saying so —
               unless the re-read found something more pressing to say. */
            void refreshConversation().then(() => {
              // Only while this Send is still the latest: a later Send owns the
              // notice now, whether it was refused or succeeded.
              if (
                activeConversationRef.current === conversationId &&
                submissionGenerationRef.current === submissionGeneration
              )
                setFailure((current) => current ?? sendFailure);
            });
          }
        }
        throw sendError;
      } finally {
        if (submissionGenerationRef.current === submissionGeneration) {
          submittingRef.current = false;
          setIsSubmittingMessage(false);
        }
      }
    },
    [clearAssistantDraft, client, refreshConversation, updateRun],
  );

  const updateGroupSettings = useCallback(
    async (input: { readonly schedule: GroupSchedule }): Promise<void> => {
      const conversationId = activeConversationRef.current;
      if (!conversationId) return;
      const generation = ++groupSettingsGenerationRef.current;
      try {
        const envelope = await client.updateGroupSettings(conversationId, {
          client_action_id: randomUuid(),
          schedule: input.schedule,
        });
        if (
          generation !== groupSettingsGenerationRef.current ||
          activeConversationRef.current !== conversationId
        ) {
          return;
        }
        ++groupSettingsGenerationRef.current;
        setGroupSettings(envelope.group_settings);
        setFailure(null);
      } catch (writeError) {
        if (activeConversationRef.current !== conversationId) return;
        setFailure(requestFailure(writeError, scopeRef.current, "write"));
        // Re-read rather than keep an optimistic value: a rejected write means
        // the server's answer, not the caller's, is the one to show.
        void refreshGroupSettings(conversationId);
      }
    },
    [client, refreshGroupSettings],
  );

  /**
   * Stop the active Run (Task 14). The server decides who may and whether
   * anything is still running; the Run arrives back as `stopping`, then ends
   * `stopped` through the ordinary Run frames.
   */
  const stopRun = useCallback(async (): Promise<void> => {
    const conversationId = activeConversationRef.current;
    const current = runRef.current;
    if (!conversationId || !current) return;
    try {
      const envelope = await client.stopRun(conversationId, current.id);
      if (activeConversationRef.current !== conversationId) return;
      // d30's rule decides: a reply that lands after the Run finished, or
      // after a newer Run started, cannot put this one back.
      updateRun(envelope.run);
      setEventCursor((cursor) => Math.max(cursor, envelope.event_cursor));
    } catch (stopError) {
      if (activeConversationRef.current === conversationId) {
        setFailure(requestFailure(stopError, scopeRef.current, "write"));
      }
    }
  }, [client, updateRun]);

  const decidePendingRequests = useCallback(
    async (decision: "resume" | "discard"): Promise<void> => {
      const conversationId = activeConversationRef.current;
      const pause = groupSettings?.pause;
      if (!conversationId || !pause) return;
      const generation = ++groupSettingsGenerationRef.current;
      try {
        const envelope =
          decision === "resume"
            ? await client.resumePendingRequests(
                conversationId,
                pause.paused_run_id,
              )
            : await client.discardPendingRequests(
                conversationId,
                pause.paused_run_id,
                pause.through_message_sequence,
              );
        if (
          generation !== groupSettingsGenerationRef.current ||
          activeConversationRef.current !== conversationId
        ) {
          return;
        }
        setGroupSettings(envelope.group_settings);
        setFailure(null);
      } catch (decisionError) {
        if (activeConversationRef.current !== conversationId) return;
        setFailure(requestFailure(decisionError, scopeRef.current, "write"));
        void refreshGroupSettings(conversationId);
      }
    },
    [client, groupSettings?.pause, refreshGroupSettings],
  );

  const resumePendingRequests = useCallback(
    () => decidePendingRequests("resume"),
    [decidePendingRequests],
  );
  const discardPendingRequests = useCallback(
    () => decidePendingRequests("discard"),
    [decidePendingRequests],
  );

  /**
   * This tab's typing state (Task 15). Fire-and-forget: typing must never
   * block or fail a send, so every error is swallowed. The caller throttles.
   */
  const signalTyping = useCallback(
    (state: "typing" | "idle"): void => {
      const conversationId = activeConversationRef.current;
      if (!conversationId) return;
      void client
        .signalTyping(conversationId, { state, sessionId: typingSessionId })
        .catch(() => undefined);
    },
    [client, typingSessionId],
  );

  /** Messages that mention the viewer, across the whole history (Task 6). */
  const loadMentionIndex =
    useCallback(async (): Promise<MentionIndex | null> => {
      const conversationId = activeConversationRef.current;
      if (!conversationId) return null;
      try {
        const index = await client.getMyMentions(conversationId);
        return activeConversationRef.current === conversationId ? index : null;
      } catch {
        return null;
      }
    }, [client]);

  /**
   * Load older pages until `sequence` is in the transcript (Tasks 6 and 7:
   * jumping to a mention or a referenced message outside the loaded
   * history). Resolves whether it is now loaded.
   */
  const loadMessagesThrough = useCallback(
    async (sequence: number): Promise<boolean> => {
      const conversationId = activeConversationRef.current;
      const generation = loadGenerationRef.current;
      if (!conversationId || pagingGenerationRef.current === generation)
        return false;
      pagingGenerationRef.current = generation;
      setIsLoadingMessages(true);
      try {
        while (
          beforeSequenceRef.current !== null &&
          beforeSequenceRef.current > sequence
        ) {
          const page = await client.fetchMessages(conversationId, {
            limit: 100,
            beforeSequence: beforeSequenceRef.current,
          });
          if (
            generation !== loadGenerationRef.current ||
            activeConversationRef.current !== conversationId
          )
            return false;
          beforeSequenceRef.current = page.next_before_sequence;
          setHasMoreMessages(page.next_before_sequence !== null);
          setMessages((current) => mergeMessages(page.messages, current));
        }
        return true;
      } catch (historyError) {
        if (
          generation === loadGenerationRef.current &&
          activeConversationRef.current === conversationId
        ) {
          setFailure(requestFailure(historyError, scopeRef.current));
        }
        return false;
      } finally {
        if (generation === loadGenerationRef.current) {
          pagingGenerationRef.current = null;
          setIsLoadingMessages(false);
        }
      }
    },
    [client],
  );

  const loadBackgroundCanvasSettings = useCallback(async (): Promise<void> => {
    const conversationId = activeConversationRef.current;
    const currentScope = scopeRef.current;
    if (
      !conversationId ||
      currentScope?.conversationType !== "workspace" ||
      currentScope.workspaceType !== "solve"
    ) {
      setBackgroundCanvasSettings(null);
      backgroundCanvasSettingsRef.current = null;
      return;
    }
    const generation = ++backgroundCanvasSettingsGenerationRef.current;
    try {
      const value = await client.getConversationSpecialistSettings(
        conversationId,
        SOLVE_CANVAS_SPECIALIST_ID,
      );
      if (
        generation !== backgroundCanvasSettingsGenerationRef.current ||
        activeConversationRef.current !== conversationId
      ) {
        return;
      }
      setBackgroundCanvasSettings(value);
      backgroundCanvasSettingsRef.current = value;
      setFailure(null);
    } catch (settingsError) {
      if (
        generation === backgroundCanvasSettingsGenerationRef.current &&
        activeConversationRef.current === conversationId
      ) {
        setFailure(requestFailure(settingsError, scopeRef.current));
      }
    }
  }, [client]);

  const setBackgroundCanvasModelPolicy = useCallback(
    (modelPolicyId: ModelPolicyId): Promise<void> => {
      const conversationId = activeConversationRef.current;
      if (!conversationId) return Promise.resolve();
      const generation = ++backgroundCanvasSettingsGenerationRef.current;
      const write = backgroundCanvasSettingsWriteRef.current
        .catch(() => undefined)
        .then(async () => {
          try {
            const value = await client.updateConversationSpecialistSettings(
              conversationId,
              SOLVE_CANVAS_SPECIALIST_ID,
              modelPolicyId,
            );
            if (
              generation !== backgroundCanvasSettingsGenerationRef.current ||
              activeConversationRef.current !== conversationId
            ) {
              return;
            }
            setBackgroundCanvasSettings(value);
            backgroundCanvasSettingsRef.current = value;
            setFailure(null);
          } catch (settingsError) {
            if (
              activeConversationRef.current !== conversationId ||
              generation !== backgroundCanvasSettingsGenerationRef.current
            ) {
              return;
            }
            setFailure(
              requestFailure(settingsError, scopeRef.current, "write"),
            );
            void loadBackgroundCanvasSettings();
          }
        });
      backgroundCanvasSettingsWriteRef.current = write;
      return write;
    },
    [client, loadBackgroundCanvasSettings],
  );

  const setConversationProfile = useCallback(
    (profileId: string | null): Promise<void> => {
      const conversationId = activeConversationRef.current;
      if (!conversationId || !conversationProfileRef.current)
        return Promise.resolve();
      const write = conversationProfileWriteRef.current
        .catch(() => undefined)
        .then(async () => {
          const current = conversationProfileRef.current;
          if (!current || activeConversationRef.current !== conversationId)
            return;
          try {
            const value = await client.updateConversationProfile(
              conversationId,
              profileId,
              current.settings.version,
            );
            if (activeConversationRef.current === conversationId) {
              setConversationProfileState(value);
              conversationProfileRef.current = value;
              setFailure(null);
            }
          } catch (profileError) {
            if (activeConversationRef.current === conversationId) {
              setFailure(
                requestFailure(profileError, scopeRef.current, "write"),
              );
              try {
                const value =
                  await client.getConversationProfile(conversationId);
                if (activeConversationRef.current === conversationId) {
                  setConversationProfileState(value);
                  conversationProfileRef.current = value;
                }
              } catch {
                // Preserve the original save error. Recovery is best-effort;
                // a second network failure must not reject the serialized
                // write or become an unhandled promise rejection.
              }
            }
          }
        });
      conversationProfileWriteRef.current = write;
      return write;
    },
    [client],
  );

  const setBackgroundCanvasProfile = useCallback(
    (profileId: string | null): Promise<void> => {
      const conversationId = activeConversationRef.current;
      if (!conversationId || !backgroundCanvasSettingsRef.current)
        return Promise.resolve();
      const generation = ++backgroundCanvasSettingsGenerationRef.current;
      const write = backgroundCanvasSettingsWriteRef.current
        .catch(() => undefined)
        .then(async () => {
          const current = backgroundCanvasSettingsRef.current;
          if (!current || activeConversationRef.current !== conversationId)
            return;
          try {
            const value = await client.updateConversationSpecialistSettings(
              conversationId,
              SOLVE_CANVAS_SPECIALIST_ID,
              {
                profile_id: profileId,
                expected_profile_version: current.profile_version,
              },
            );
            if (activeConversationRef.current === conversationId) {
              backgroundCanvasSettingsRef.current = value;
              if (
                generation === backgroundCanvasSettingsGenerationRef.current
              ) {
                setBackgroundCanvasSettings(value);
                setFailure(null);
              }
            }
          } catch (profileError) {
            if (
              generation === backgroundCanvasSettingsGenerationRef.current &&
              activeConversationRef.current === conversationId
            ) {
              setFailure(
                requestFailure(profileError, scopeRef.current, "write"),
              );
              void loadBackgroundCanvasSettings();
            }
          }
        });
      backgroundCanvasSettingsWriteRef.current = write;
      return write;
    },
    [client, loadBackgroundCanvasSettings],
  );

  const setSolveResponseStyle = useCallback(
    (responseStyle: SolveResponseStyle): Promise<void> => {
      const conversationId = activeConversationRef.current;
      if (!conversationId) return Promise.resolve();
      const write = solveResponseStyleWriteRef.current
        .catch(() => undefined)
        .then(async () => {
          try {
            const value = await client.updateSolveResponseStyle(
              conversationId,
              responseStyle,
            );
            if (activeConversationRef.current !== conversationId) return;
            // Advanced only for a write the server accepted AND that is still the
            // active conversation's. A refused write, or one the caller navigated
            // away from, leaves the generation alone so it cannot invalidate an
            // in-flight hydration that is perfectly current.
            ++solveResponseStyleWriteGenerationRef.current;
            setSolveResponseStyleOverride({
              conversationId,
              style: value.response_style,
            });
            setFailure(null);
          } catch (styleError) {
            if (activeConversationRef.current !== conversationId) return;
            // The server refused (or is down). Surface it and leave the displayed
            // value alone — the rail must never show a tone that is not stored.
            setFailure(requestFailure(styleError, scopeRef.current, "write"));
          }
        });
      solveResponseStyleWriteRef.current = write;
      return write;
    },
    [client],
  );

  const setSolveWebSearchEnabled = useCallback(
    (enabled: boolean): Promise<void> => {
      const conversationId = activeConversationRef.current;
      if (!conversationId) return Promise.resolve();
      const write = solveWebSearchWriteRef.current
        .catch(() => undefined)
        .then(async () => {
          try {
            const value = await client.updateWebSearch(conversationId, enabled);
            if (activeConversationRef.current !== conversationId) return;
            ++solveWebSearchWriteGenerationRef.current;
            setSolveWebSearchOverride({
              conversationId,
              enabled: value.enabled,
            });
            setFailure(null);
          } catch (settingsError) {
            if (activeConversationRef.current !== conversationId) return;
            setFailure(
              requestFailure(settingsError, scopeRef.current, "write"),
            );
          }
        });
      solveWebSearchWriteRef.current = write;
      return write;
    },
    [client],
  );

  const decideDocumentWait = useCallback(
    async (decision: "wait" | "continue"): Promise<void> => {
      const conversationId = activeConversationRef.current;
      const currentRun = runRef.current;
      if (
        !conversationId ||
        documentWaitDecisionPendingRef.current ||
        currentRun?.status !== "waiting_documents" ||
        currentRun.document_wait?.decision !== null
      ) {
        return;
      }
      documentWaitDecisionPendingRef.current = true;
      try {
        const result = await client.decideDocumentWait(
          conversationId,
          currentRun.id,
          { clientActionId: randomUuid(), decision },
        );
        if (activeConversationRef.current !== conversationId) return;
        updateRun(result.run);
        setEventCursor((current) => Math.max(current, result.event_cursor));
        setFailure(null);
      } catch (decisionError) {
        if (activeConversationRef.current === conversationId) {
          setFailure(requestFailure(decisionError, scopeRef.current, "write"));
        }
      } finally {
        documentWaitDecisionPendingRef.current = false;
      }
    },
    [client, updateRun],
  );

  const startNext = useCallback(async (): Promise<void> => {
    const conversationId = activeConversationRef.current;
    if (!conversationId || scopeRef.current?.conversationType !== "workspace") {
      return;
    }
    try {
      const result = await client.createNextRun(
        conversationId,
        randomUuid(),
        undefined,
        browserCurrentDate(),
      );
      if (activeConversationRef.current !== conversationId) return;
      clearAssistantDraft();
      updateRun(result.run);
      setEventCursor((current) => Math.max(current, result.event_cursor));
      setFailure(null);
    } catch (runError) {
      if (activeConversationRef.current === conversationId) {
        setFailure(requestFailure(runError, scopeRef.current, "write"));
        // Same rule as `sendMessage` and `decideProposal`: a 403 means the
        // server refused an action the client believed it held, so re-read the
        // authority rather than leaving a stale affordance. Never replayed.
        if (
          runError instanceof PlayerAIProblem &&
          runError.problem.status === 403
        )
          void refreshConversation(true);
      }
      throw runError;
    }
  }, [clearAssistantDraft, client, refreshConversation, updateRun]);

  const decideProposal = useCallback(
    async (
      proposalId: string,
      decision: "approve" | "reject",
      reason?: string,
      /** Bounded narrowing of a selectable proposal (see the client type). */
      selection?: ProposalDecisionSelection,
      /** An edited value set, on `approve` only (see the client type). */
      revision?: ProposalRevisionInput,
    ): Promise<boolean> => {
      const conversationId = activeConversationRef.current;
      if (!conversationId) return false;
      try {
        const result = await client.decideProposal(conversationId, proposalId, {
          clientActionId: randomUuid(),
          decision,
          ...(reason ? { reason } : {}),
          ...(selection ? { selection } : {}),
          ...(revision ? { revision } : {}),
        });
        if (activeConversationRef.current !== conversationId) return false;
        // For a revision this is the SUCCESSOR, which carries a different id.
        // The upsert adds it rather than replacing the original; the original's
        // own `proposal.stale` frame retires it, so both are accounted for.
        proposalRevisionsRef.current.set(
          result.proposal.id,
          (proposalRevisionsRef.current.get(result.proposal.id) ?? 0) + 1,
        );
        setProposals((current) => upsertProposal(current, result.proposal));
        setFailure(null);
        return true;
      } catch (decisionError) {
        if (activeConversationRef.current === conversationId) {
          setFailure(requestFailure(decisionError, scopeRef.current, "write"));
          if (
            decisionError instanceof PlayerAIProblem &&
            decisionError.problem.status === 403
          )
            await refreshConversation(true);
        }
        return false;
      }
    },
    [client, refreshConversation],
  );

  const decideProposalBatch = useCallback(
    async (
      approvalBatchId: string,
      items: ProposalBatchDecisionItem[],
    ): Promise<boolean> => {
      const conversationId = activeConversationRef.current;
      if (!conversationId) return false;
      try {
        const result = await client.decideProposalBatch(
          conversationId,
          approvalBatchId,
          {
            clientActionId: randomUuid(),
            items,
          },
        );
        if (activeConversationRef.current !== conversationId) return false;
        for (const proposal of result.proposals)
          proposalRevisionsRef.current.set(
            proposal.id,
            (proposalRevisionsRef.current.get(proposal.id) ?? 0) + 1,
          );
        setProposals((current) =>
          result.proposals.reduce(upsertProposal, current),
        );
        setFailure(null);
        return true;
      } catch (decisionError) {
        if (activeConversationRef.current === conversationId) {
          setFailure(requestFailure(decisionError, scopeRef.current, "write"));
          if (
            decisionError instanceof PlayerAIProblem &&
            decisionError.problem.status === 403
          )
            await refreshConversation(true);
        }
        return false;
      }
    },
    [client, refreshConversation],
  );

  const editPlatformActionBatch = useCallback(
    async (actions: readonly PlatformAction[]): Promise<void> => {
      const conversationId = activeConversationRef.current;
      const current = platformActionBatch;
      if (
        !conversationId ||
        !current ||
        current.status !== "pending_confirmation" ||
        actions.length === 0
      ) {
        return;
      }
      try {
        const updated = await client.upsertPlatformActionBatch(conversationId, {
          client_action_id: randomUuid(),
          batch_id: current.id,
          expected_revision: current.revision,
          actions,
        });
        if (activeConversationRef.current === conversationId) {
          setPlatformActionBatch(updated);
          setFailure(null);
        }
      } catch (editError) {
        if (activeConversationRef.current === conversationId) {
          setFailure(requestFailure(editError, scopeRef.current, "write"));
          void refreshPlatformActionBatch(conversationId);
        }
      }
    },
    [client, platformActionBatch, refreshPlatformActionBatch],
  );

  const applyPlatformActionBatch = useCallback(async (): Promise<void> => {
    const conversationId = activeConversationRef.current;
    const current = platformActionBatch;
    if (
      !conversationId ||
      !current ||
      current.status !== "pending_confirmation"
    ) {
      return;
    }
    setPlatformActionBatch({ ...current, status: "applying" });
    try {
      const applied = await client.applyPlatformActionBatch(
        conversationId,
        current.id,
        {
          client_action_id: randomUuid(),
          frozen_hash: current.frozen.frozen_hash,
        },
      );
      if (activeConversationRef.current === conversationId) {
        setPlatformActionBatch(applied);
        setFailure(null);
      }
    } catch (applyError) {
      if (activeConversationRef.current === conversationId) {
        setPlatformActionBatch(current);
        setFailure(requestFailure(applyError, scopeRef.current, "write"));
        void refreshPlatformActionBatch(conversationId);
      }
    }
  }, [client, platformActionBatch, refreshPlatformActionBatch]);

  const loadPlatformActionBatch = useCallback(
    async (batchId: string): Promise<void> => {
      const conversationId = activeConversationRef.current;
      if (!conversationId) return;
      try {
        const batch = await client.getPlatformActionBatch(
          conversationId,
          batchId,
        );
        if (activeConversationRef.current === conversationId) {
          setPlatformActionBatch(batch);
          setFailure(null);
        }
      } catch (readError) {
        if (activeConversationRef.current === conversationId) {
          setFailure(requestFailure(readError, scopeRef.current));
        }
      }
    },
    [client],
  );

  const loadOlderMessages = useCallback(async (): Promise<void> => {
    const conversationId = activeConversationRef.current;
    const beforeSequence = beforeSequenceRef.current;
    const generation = loadGenerationRef.current;
    if (
      !conversationId ||
      beforeSequence === null ||
      pagingGenerationRef.current === generation
    )
      return;
    pagingGenerationRef.current = generation;
    setIsLoadingMessages(true);
    try {
      const page = await client.fetchMessages(conversationId, {
        limit: 50,
        beforeSequence,
      });
      if (
        generation !== loadGenerationRef.current ||
        activeConversationRef.current !== conversationId
      )
        return;
      beforeSequenceRef.current = page.next_before_sequence;
      setHasMoreMessages(page.next_before_sequence !== null);
      setMessages((current) => mergeMessages(page.messages, current));
    } catch (historyError) {
      if (
        generation === loadGenerationRef.current &&
        activeConversationRef.current === conversationId
      ) {
        setFailure(requestFailure(historyError, scopeRef.current));
      }
    } finally {
      if (generation === loadGenerationRef.current) {
        pagingGenerationRef.current = null;
        setIsLoadingMessages(false);
      }
    }
  }, [client]);

  const saveMessageFeedback = useCallback(
    async (
      conversationId: string,
      messageId: string,
      input: UpsertMessageFeedbackInput,
    ): Promise<MessageFeedback> => {
      if (activeConversationRef.current !== conversationId) {
        throw new Error(
          "The feedback target is no longer the active conversation.",
        );
      }
      const feedback = await client.putMessageFeedback(
        conversationId,
        messageId,
        input,
      );
      if (
        activeConversationRef.current !== conversationId ||
        feedback.conversation_id !== conversationId ||
        feedback.message_id !== messageId
      ) {
        throw new Error("Player AI returned feedback for a different message.");
      }
      setMessages((current) =>
        current.map((message) =>
          message.id === messageId
            ? { ...message, viewer_feedback: feedback }
            : message,
        ),
      );
      return feedback;
    },
    [client],
  );

  useEffect(
    () =>
      client.subscribeSettings((value) => {
        setSettings(value);
      }),
    [client],
  );

  const setAutoApproveInternalTools = useCallback(
    async (enabled: boolean): Promise<void> => {
      const writeGeneration = ++autoApproveWriteGenerationRef.current;
      try {
        const updated = await client.updateSettings({
          auto_approve_internal_tools: enabled,
        });
        if (autoApproveWriteGenerationRef.current === writeGeneration) {
          setSettings((current) => ({
            ...current,
            auto_approve_internal_tools: updated.auto_approve_internal_tools,
          }));
          setFailure(null);
        }
      } catch (settingsError) {
        if (autoApproveWriteGenerationRef.current === writeGeneration) {
          setFailure(requestFailure(settingsError, scopeRef.current, "write"));
        }
      }
    },
    [client],
  );

  const setModelPolicy = useCallback(
    async (modelPolicyId: ModelPolicyId): Promise<void> => {
      const writeGeneration = ++modelPolicyWriteGenerationRef.current;
      try {
        const updated = await client.updateSettings({
          model_policy_id: modelPolicyId,
        });
        if (modelPolicyWriteGenerationRef.current === writeGeneration) {
          setSettings((current) => ({
            ...current,
            model_policy_id: updated.model_policy_id,
          }));
          setFailure(null);
        }
      } catch (settingsError) {
        if (modelPolicyWriteGenerationRef.current === writeGeneration) {
          setFailure(requestFailure(settingsError, scopeRef.current, "write"));
        }
      }
    },
    [client],
  );

  const scopeConversationType = scope?.conversationType;
  const scopeWorkspaceType =
    scope && "workspaceId" in scope ? scope.workspaceType : undefined;
  const scopeWorkspaceId =
    scope && "workspaceId" in scope ? scope.workspaceId : undefined;
  const hasScope = scopeConversationType !== undefined;
  useEffect(() => {
    if (!hasScope) {
      setSettings({ ...DEFAULT_USER_SETTINGS });
      setModelPolicies([]);
      return;
    }
    // getSettings publishes through the version-aware subscription above.
    void client.getSettings().catch(() => {
      // Keep the product defaults when settings are temporarily unavailable.
      // Conversation and event-stream errors remain independent.
    });
  }, [
    client,
    hasScope,
    scopeConversationType,
    scopeWorkspaceType,
    scopeWorkspaceId,
  ]);

  useEffect(() => {
    ++loadGenerationRef.current;
    disconnectRef.current?.();
    disconnectRef.current = null;
    activeConversationRef.current = null;
    todayLocalDateRef.current = null;
    conversationSurfaceRef.current = null;
    beforeSequenceRef.current = null;
    ++submissionGenerationRef.current;
    submittingRef.current = false;
    setIsSubmittingMessage(false);
    setConnectionState("idle");
    setStreamReconnectAttempt(0);
    setIsConversationLoading(Boolean(scope));
    // Claimed by THIS run, whatever set it before: a scope change supersedes an
    // in-flight setup, and a retry has already claimed it synchronously.
    initializingRef.current = Boolean(scope);
    setFailure(null);
    setInitializationError(null);
    setConversations([]);
    setActiveConversationId(null);
    setMessages([]);
    setProposals([]);
    setPlatformActionBatch(null);
    updateRun(null, RESET_RUN);
    transportRestartPendingRef.current = false;
    setCapabilities(null);
    setConversationSurface(null);
    setIsEffectiveDriver(false);
    setGroupSettings(null);
    setBackgroundCanvasSettings(null);
    setBackgroundReviewFailure(null);
    setContextNotice(null);
    setRunFailure(null);
    ++groupSettingsGenerationRef.current;
    ++backgroundCanvasSettingsGenerationRef.current;
    setEventCursor(0);
    clearAssistantDraft();
    setHasMoreMessages(false);

    if (!scope) return;
    let cancelled = false;
    /**
     * Select, and treat a real startup failure as an INITIALIZATION failure.
     *
     * `selectConversation` answers `false` both when its hydration / message /
     * proposal reads failed and when a newer load superseded it. Only the
     * first is recoverable by re-running initialization, and only the first
     * leaves a reason behind — so this rethrows into the `catch` below, which
     * is what arms "Retry connection". A superseded load falls through
     * silently, exactly as before.
     */
    const selectOrFailInitialization = async (
      conversationId: string,
    ): Promise<void> => {
      const selected = await selectConversation(conversationId);
      if (selected) return;
      const carried = selectFailureRef.current;
      if (carried !== null) throw new CarriedPlayerAIFailure(carried);
    };
    const initialize = async (): Promise<void> => {
      try {
        if (scope.conversationType === "workspace") {
          const result = await client.resolveWorkspaceConversation(
            scope.workspaceType,
            scope.workspaceId,
          );
          if (cancelled) return;
          setConversations([result.conversation]);
          await selectOrFailInitialization(result.conversation.id);
          return;
        }
        if (!("workspaceId" in scope)) {
          const [page, today] = await Promise.all([
            client.listPrivateConversations({ limit: 50 }),
            client.resolveTodayConversation(browserTimeZone()),
          ]);
          if (cancelled) return;
          todayLocalDateRef.current = today.local_date;
          setConversations(
            upsertConversation(page.conversations, today.conversation, true),
          );
          await selectOrFailInitialization(today.conversation.id);
          return;
        }
        if (hasSingletonPrivateConversation(scope.workspaceType)) {
          // Ask the SERVER which surface is active and open it. Deliberately
          // not list-then-create: that races two tabs into two conversations,
          // and it would also let a client keep opening a private thread after
          // the product had moved the workspace to its shared one.
          const active = await client.resolveActiveConversation(
            scope.workspaceType,
            scope.workspaceId,
          );
          if (cancelled) return;
          conversationSurfaceRef.current = active.conversation_mode;
          setConversationSurface(active.conversation_mode);
          setIsEffectiveDriver(active.is_effective_driver === true);
          setConversations([active.conversation]);
          await selectOrFailInitialization(active.conversation.id);
          return;
        }
        const page = await client.listWorkspacePrivateConversations(
          scope.workspaceType,
          scope.workspaceId,
          { limit: 50 },
        );
        if (cancelled) return;
        setConversations(page.conversations);
        const conversation =
          page.conversations[0] ??
          (await client.createWorkspacePrivateConversation(
            scope.workspaceType,
            scope.workspaceId,
          ));
        if (cancelled) return;
        setConversations((current) =>
          upsertConversation(current, conversation, true),
        );
        await selectOrFailInitialization(conversation.id);
      } catch (setupError) {
        if (cancelled) return;
        // `session_setup`, which is what turns a transient cause into
        // "Reconnect to try again" instead of a Try again with no Run behind
        // it. An expired session or a refused authorization keeps its own copy:
        // reconnecting does not fix either (see `SESSION_SETUP_COPY`).
        const setupFailure = requestFailure(
          setupError,
          scopeRef.current,
          "session_setup",
        );
        setConnectionState("closed");
        setIsConversationLoading(false);
        setFailure(setupFailure);
        // PB-1586: the SAME message, additionally flagged as an initialization
        // failure. Both are set rather than one replacing the other so the
        // existing `error` contract is untouched for every consumer that reads
        // it — this only tells a host that the failure is RECOVERABLE IN PLACE
        // via `retryInitialize`, which is what the panel was missing.
        setInitializationError(failureSentence(setupFailure));
      } finally {
        // Only the CURRENT run may re-arm the retry. A superseded one clearing
        // the claim would readmit the very second attempt the flag exists to
        // refuse, while the run that superseded it is still in flight.
        if (!cancelled) initializingRef.current = false;
      }
    };
    void initialize();
    return () => {
      cancelled = true;
      ++loadGenerationRef.current;
      disconnectRef.current?.();
      disconnectRef.current = null;
    };
  }, [
    client,
    scope?.conversationType,
    // Both halves of the workspace identity: two scopes differing only by
    // workspace TYPE (a plan and a workshop can share nothing but an id
    // by coincidence) must re-initialize.
    scope && "workspaceId" in scope ? scope.workspaceType : undefined,
    scope && "workspaceId" in scope ? scope.workspaceId : undefined,
    // PB-1586: an explicit retry re-runs this whole effect, reset included, so
    // a recovered dependency is picked up without a page reload.
    initializeAttempt,
    selectConversation,
    clearAssistantDraft,
    updateRun,
  ]);

  /**
   * Re-run initialization after a dependency outage (PB-1586).
   *
   * A no-op until the current setup SETTLES, claimed synchronously through
   * {@link initializingRef} — so a user hammering Retry, or a host calling it
   * twice before the effect re-runs, cannot stack overlapping setups. The
   * `cancelled` guard alone was not enough: it stops a superseded run from
   * writing state, but its requests are already out, and on the
   * list-then-create branch a second attempt lists before the first attempt's
   * POST is visible and creates a second conversation. A failed or completed
   * run releases the claim, so recovery after a failure is unaffected.
   *
   * It does not touch capabilities and cannot widen access: what a recovered
   * session may do is whatever the server's authorization answer says, exactly
   * as on a first load.
   */
  const retryInitialize = useCallback((): void => {
    if (initializingRef.current) return;
    initializingRef.current = true;
    setInitializeAttempt((attempt) => attempt + 1);
  }, []);

  useEffect(() => {
    if (
      scope?.conversationType !== "private" ||
      "workspaceId" in scope ||
      typeof document === "undefined"
    ) {
      return;
    }
    let cancelled = false;
    const handleVisible = (): void => {
      if (
        document.visibilityState !== "visible" ||
        todayLocalDateRef.current === browserCurrentDate().date
      ) {
        return;
      }
      const generation = loadGenerationRef.current;
      void client
        .resolveTodayConversation(browserTimeZone())
        .then(async (today) => {
          if (cancelled || generation !== loadGenerationRef.current) return;
          todayLocalDateRef.current = today.local_date;
          setConversations((current) =>
            upsertConversation(current, today.conversation, true),
          );
          // Returning after midnight must not interrupt the thread already on
          // screen. The new Today thread is now discoverable in the list and a
          // fresh app entry will select it; only an otherwise-empty surface may
          // select it here.
          if (activeConversationRef.current === null) {
            await selectConversation(today.conversation.id);
          }
        })
        .catch((resolutionError) => {
          if (!cancelled && generation === loadGenerationRef.current) {
            setFailure(requestFailure(resolutionError, scopeRef.current));
          }
        });
    };
    document.addEventListener("visibilitychange", handleVisible);
    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", handleVisible);
    };
  }, [
    client,
    scopeConversationType,
    scopeWorkspaceType,
    scopeWorkspaceId,
    selectConversation,
  ]);

  const webSearchEnabled =
    (solveWebSearchOverride?.conversationId === activeConversationId
      ? solveWebSearchOverride.enabled
      : null) ??
    capabilities?.web_search_enabled ??
    capabilities?.solve_web_search_enabled ??
    null;
  const canChangeWebSearch =
    !authorityBlocked && (capabilities?.can_change_web_search ?? false);

  return {
    connectionState,
    streamReconnectAttempt,
    error,
    failure,
    authorityStatus,
    initializationError,
    retryInitialize,
    conversations,
    activeConversationId,
    messages,
    proposals,
    platformActionBatch,
    run,
    capabilities:
      authorityBlocked && capabilities
        ? {
            ...capabilities,
            can_post_message: false,
            can_start_run: false,
            can_decide_proposal: false,
            can_change_response_style: false,
            can_change_web_search: false,
            can_control_runs: false,
            ...(capabilities.collaboration
              ? {
                  collaboration: {
                    ...capabilities.collaboration,
                    can_mention_player_ai: false,
                    can_configure_group_ai: false,
                    can_decide_group_proposal: false,
                  },
                }
              : {}),
          }
        : capabilities,
    conversationSurface,
    isEffectiveDriver,
    eventCursor,
    assistantDraft,
    runProgress,
    runActivities,
    runFailure,
    settings,
    modelPolicies,
    collaborationMode: (capabilities?.collaboration?.mode ??
      "individual") as CollaborationMode,
    groupSettings:
      groupSettings && authorityBlocked
        ? { ...groupSettings, can_configure: false }
        : groupSettings,
    solveResponseStyle:
      (solveResponseStyleOverride?.conversationId === activeConversationId
        ? solveResponseStyleOverride.style
        : null) ??
      capabilities?.solve_response_style ??
      null,
    canChangeSolveResponseStyle:
      !authorityBlocked && (capabilities?.can_change_response_style ?? false),
    setSolveResponseStyle,
    webSearchEnabled,
    canChangeWebSearch,
    setWebSearchEnabled: setSolveWebSearchEnabled,
    solveWebSearchEnabled: webSearchEnabled,
    canChangeSolveWebSearch: canChangeWebSearch,
    setSolveWebSearchEnabled,
    backgroundCanvasSettings:
      backgroundCanvasSettings && authorityBlocked
        ? { ...backgroundCanvasSettings, can_change: false }
        : backgroundCanvasSettings,
    conversationProfile:
      conversationProfile && authorityBlocked
        ? {
            ...conversationProfile,
            settings: { ...conversationProfile.settings, can_change: false },
          }
        : conversationProfile,
    backgroundReviewFailure,
    contextNotice,
    retryContextCompaction,
    canvasStatusLabel: canvasWork.label,
    canvasStatus: canvasWork.status,
    canvasStatusUnavailable: canvasWork.unavailable,
    retryCanvasStatus: canvasWork.retry,
    isRunActive,
    isConversationLoading,
    isSubmittingMessage,
    hasMoreMessages,
    isLoadingMessages,
    createConversation,
    selectConversation,
    refreshConversation,
    sendMessage,
    decideDocumentWait,
    startNext,
    updateGroupSettings,
    loadBackgroundCanvasSettings,
    setBackgroundCanvasModelPolicy,
    setConversationProfile,
    setBackgroundCanvasProfile,
    decideProposal,
    decideProposalBatch,
    editPlatformActionBatch,
    applyPlatformActionBatch,
    loadPlatformActionBatch,
    setAutoApproveInternalTools,
    setModelPolicy,
    saveMessageFeedback,
    loadOlderMessages,
    loadMessagesThrough,
    loadMentionIndex,
    stopRun,
    resumePendingRequests,
    discardPendingRequests,
    typingParticipants,
    signalTyping,
  };
}

function browserCurrentDate(): import("./types.js").BrowserCurrentDate {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  const resolved = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return {
    date: `${year}-${month}-${day}`,
    time_zone: resolved || "UTC",
    source: "browser",
  };
}

function browserTimeZone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
}

function upsertConversation(
  values: Conversation[],
  candidate: Conversation,
  first = false,
): Conversation[] {
  const remaining = values.filter((value) => value.id !== candidate.id);
  return first
    ? [candidate, ...remaining]
    : [...remaining, candidate].sort(byUpdatedAt);
}

function upsertMessage(values: Message[], candidate: Message): Message[] {
  return mergeMessages(values, [candidate]);
}

function mergeMessages(left: Message[], right: Message[]): Message[] {
  const values = new Map<string, Message>();
  for (const item of [...left, ...right]) {
    const existing = values.get(item.id);
    values.set(item.id, {
      ...item,
      viewer_feedback:
        item.viewer_feedback ?? existing?.viewer_feedback ?? null,
    });
  }
  return [...values.values()].sort(byMessageSequence);
}

function upsertProposal(values: Proposal[], candidate: Proposal): Proposal[] {
  const remaining = values.filter((value) => value.id !== candidate.id);
  return [...remaining, candidate].sort(byCreatedAt);
}

/**
 * Upsert by activity id so at-least-once and out-of-order delivery converge on
 * one row per activity — and never regress it. Delivery order is not causal
 * order, so a redelivered `started` frame must not overwrite the `completed`
 * or `failed` transition that already arrived, or the row spins forever.
 */
function upsertActivity(
  values: RunActivity[],
  candidate: RunActivity,
): RunActivity[] {
  const existing = values.find(
    (value) => value.activity_id === candidate.activity_id,
  );
  if (existing !== undefined && !supersedesActivity(candidate, existing)) {
    // Identical or stale frame: keep the same reference so React does not
    // re-render on a redelivery that changes nothing.
    return values;
  }
  const remaining =
    existing === undefined
      ? values
      : values.filter((value) => value.activity_id !== candidate.activity_id);
  return sortActivities([...remaining, candidate]);
}

function supersedesActivity(
  candidate: RunActivity,
  existing: RunActivity,
): boolean {
  if (candidate.attempt_epoch !== existing.attempt_epoch) {
    return candidate.attempt_epoch > existing.attempt_epoch;
  }
  const candidateAt = Date.parse(candidate.updated_at);
  const existingAt = Date.parse(existing.updated_at);
  if (
    Number.isFinite(candidateAt) &&
    Number.isFinite(existingAt) &&
    candidateAt !== existingAt
  ) {
    return candidateAt > existingAt;
  }
  // Same instant, or a timestamp we cannot compare: a terminal transition is
  // the later one by construction, and two frames of the same kind carry the
  // same meaning, so keeping the incumbent stays deterministic.
  return isTerminalActivity(candidate) && !isTerminalActivity(existing);
}

function isTerminalActivity(activity: RunActivity): boolean {
  return activity.status === "completed" || activity.status === "failed";
}

function sortActivities(values: RunActivity[]): RunActivity[] {
  return [...values].sort(byActivityOrder).slice(-MAX_ACTIVE_ACTIVITIES);
}

function byActivityOrder(left: RunActivity, right: RunActivity): number {
  const startedAt =
    Date.parse(left.started_at) - Date.parse(right.started_at) || 0;
  return startedAt !== 0
    ? startedAt
    : left.activity_id.localeCompare(right.activity_id);
}

/**
 * Accepts a hydration record or an SSE frame. Anything malformed is dropped
 * rather than rendered: the UI must not claim work it cannot describe.
 */
function toRunActivity(value: unknown): RunActivity | null {
  if (typeof value !== "object" || value === null) return null;
  const candidate = value as Record<string, unknown>;
  const activityId = candidate.activity_id;
  const attemptEpoch = eventNumber(candidate.attempt_epoch);
  const kind = candidate.kind;
  const status = candidate.status;
  const startedAt = candidate.started_at;
  if (
    typeof activityId !== "string" ||
    activityId.length === 0 ||
    attemptEpoch === null ||
    attemptEpoch < 1 ||
    !isTransportableActivityKind(kind) ||
    typeof status !== "string" ||
    !RUN_ACTIVITY_STATUSES.has(status as RunActivityStatus) ||
    typeof startedAt !== "string" ||
    Number.isNaN(Date.parse(startedAt))
  ) {
    return null;
  }
  const updatedAt = candidate.updated_at ?? candidate.occurred_at;
  return {
    activity_id: activityId,
    attempt_epoch: attemptEpoch,
    kind,
    status: status as RunActivityStatus,
    source_refs: toGroundingSourceRefs(candidate.source_refs),
    failure_code:
      typeof candidate.failure_code === "string"
        ? candidate.failure_code
        : null,
    // The three trusted-metadata fields DEGRADE, never reject. A malformed or
    // unrecognised value must not cost the frame: dropping it would strand a
    // spinner that never completes, which is the same failure the additive
    // `kind` union exists to prevent.
    service_kind: toActivityServiceKind(candidate.service_kind),
    safe_count: toActivitySafeCount(candidate.safe_count),
    safe_label: toActivitySafeLabel(candidate.safe_label),
    started_at: startedAt,
    updated_at: typeof updatedAt === "string" ? updatedAt : startedAt,
  };
}

/**
 * Narrow to the CLOSED service set, or `null`.
 *
 * A service kind selects a NAME the user reads as Playbook's own, so a value
 * this bundle cannot vouch for is discarded rather than passed through — the
 * opposite of `kind`, which selects a sentence and has a safe generic fallback.
 */
function toActivityServiceKind(value: unknown): RunActivityServiceKind | null {
  return value === "jira" || value === "confluence" ? value : null;
}

/** A whole count inside the service's own bound, or `null` for "one". */
function toActivitySafeCount(value: unknown): number | null {
  return typeof value === "number" &&
    Number.isInteger(value) &&
    value >= 1 &&
    value <= MAX_RUN_ACTIVITY_SAFE_COUNT
    ? value
    : null;
}

/**
 * A bounded label, or `null`.
 *
 * Bounded here even though nothing populates it yet: the parser is what a
 * future writer's output arrives through, and a length check added later is a
 * check that was missing for every release in between.
 */
function toActivitySafeLabel(value: unknown): string | null {
  return typeof value === "string" &&
    value.length > 0 &&
    value.length <= MAX_RUN_ACTIVITY_SAFE_LABEL_LENGTH
    ? value
    : null;
}

function toGroundingSourceRefs(value: unknown): GroundingSourceRef[] {
  if (!Array.isArray(value)) return [];
  return value.flatMap((item) => {
    if (typeof item !== "object" || item === null) return [];
    const ref = item as Record<string, unknown>;
    return typeof ref.source_type === "string" &&
      GROUNDING_SOURCE_TYPES.has(ref.source_type) &&
      typeof ref.source_id === "string" &&
      ref.source_id.length > 0
      ? [
          {
            source_type: ref.source_type as GroundingSourceRef["source_type"],
            source_id: ref.source_id,
          },
        ]
      : [];
  });
}

function byMessageSequence(left: Message, right: Message): number {
  return left.sequence - right.sequence;
}

function byCreatedAt(
  left: { created_at: string },
  right: { created_at: string },
): number {
  return Date.parse(left.created_at) - Date.parse(right.created_at);
}

function byUpdatedAt(left: Conversation, right: Conversation): number {
  return Date.parse(right.updated_at) - Date.parse(left.updated_at);
}

function eventNumber(value: unknown): number | null {
  return typeof value === "number" && Number.isSafeInteger(value) && value >= 0
    ? value
    : null;
}

function isMessage(value: unknown): value is Message {
  return (
    typeof value === "object" &&
    value !== null &&
    typeof (value as Message).id === "string" &&
    typeof (value as Message).sequence === "number"
  );
}

function isRun(value: unknown): value is Run {
  return (
    typeof value === "object" &&
    value !== null &&
    typeof (value as Run).id === "string" &&
    typeof (value as Run).status === "string"
  );
}

/**
 * Where a Run update came from (d30). A live frame or a write's reply is an
 * OBSERVATION; a hydration or refresh carries the observation count it
 * sampled before the read; a conversation switch starts again from nothing.
 */
type RunUpdateSource =
  | { readonly kind: "observed" }
  /** A read nothing overtook between its issue and its answer. */
  | { readonly kind: "read" }
  | { readonly kind: "reset" };

/** A Run read's place in time, taken when it is issued. */
interface RunRead {
  readonly observation: number;
  readonly sequence: number;
  /** The Run held at issue; `undefined` for the initial load. */
  readonly heldRunId: string | null | undefined;
}

const OBSERVED_RUN: RunUpdateSource = { kind: "observed" };
const READ_RUN: RunUpdateSource = { kind: "read" };
const RESET_RUN: RunUpdateSource = { kind: "reset" };

/**
 * d30's newer-only rule for one candidate: the Run to keep, or `null` when
 * the candidate is older than what this client already holds.
 */
function admitRun(
  current: Run | null,
  retired: Pick<Run, "id" | "created_at"> | null,
  next: Run,
): Run | null {
  if (current !== null && current.id === next.id) {
    if (
      !ACTIVE_RUN_STATUSES.has(current.status) &&
      ACTIVE_RUN_STATUSES.has(next.status)
    ) {
      return null;
    }
    if (next.attempt_epoch < current.attempt_epoch) return null;
    return current.stop_requested_at && !next.stop_requested_at
      ? { ...next, stop_requested_at: current.stop_requested_at }
      : next;
  }
  // A Run a read already retired is over: only its terminal state may land.
  if (retired?.id === next.id && ACTIVE_RUN_STATUSES.has(next.status)) {
    return null;
  }
  const incumbent = current ?? retired;
  if (
    incumbent !== null &&
    incumbent.id !== next.id &&
    isEarlier(next.created_at, incumbent.created_at)
  ) {
    return null;
  }
  return next;
}

/** Only two readable instants compare; anything else is not "older". */
function isEarlier(candidate: string, incumbent: string): boolean {
  const candidateAt = Date.parse(candidate);
  const incumbentAt = Date.parse(incumbent);
  return (
    Number.isFinite(candidateAt) &&
    Number.isFinite(incumbentAt) &&
    candidateAt < incumbentAt
  );
}

function isProposal(value: unknown): value is Proposal {
  return (
    typeof value === "object" &&
    value !== null &&
    typeof (value as Proposal).id === "string" &&
    typeof (value as Proposal).status === "string"
  );
}

/** A typing frame's participants, dropping anything malformed (Task 15). */
function toTypingParticipants(value: unknown): TypingParticipant[] {
  if (!Array.isArray(value)) return [];
  return value.flatMap((candidate: unknown) => {
    if (typeof candidate !== "object" || candidate === null) return [];
    const record = candidate as Record<string, unknown>;
    return typeof record.user_id === "string" &&
      typeof record.display_name === "string" &&
      typeof record.expires_at === "string"
      ? [
          {
            user_id: record.user_id,
            display_name: record.display_name,
            expires_at: record.expires_at,
          },
        ]
      : [];
  });
}

function randomUuid(): string {
  return crypto.randomUUID();
}
